/*
 * Alaska Cold estimator (sections/ac-estimator.liquid). Loaded with defer after ac-site.js.
 *
 * Four rule-based modules behind one launcher: budget estimate, what size do I need,
 * my cold room has a problem, spread the cost. No figure is invented here: every number
 * comes from the estimator config (shop metafield custom.estimator_pricing, or the
 * fallback file assets/ac-estimator-pricing.json). docs/estimator.md explains the rules.
 *
 * The first part of this file is the engine: plain functions with no page access, so
 * tests/ac-estimator.test.js can run them in Node. The second part is the interface.
 */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Engine                                                               */
  /* ------------------------------------------------------------------ */

  const SQFT_PER_M2 = 10.7639;
  const EPSILON = 1e-9;

  function roundDown(value, step) {
    return Math.floor(value / step + EPSILON) * step;
  }

  function roundUp(value, step) {
    return Math.ceil(value / step - EPSILON) * step;
  }

  function toTenths(value) {
    return Math.round(value * 10) / 10;
  }

  function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  /* Floor area in m² from the "How big?" answer: dimensions or an area in m² or sq ft. */
  function areaFromSize(size) {
    if (!size) return 0;
    if (size.mode === 'dims') {
      return isNumber(size.w) && isNumber(size.l) && size.w > 0 && size.l > 0 ? size.w * size.l : 0;
    }
    if (size.mode === 'area') {
      if (!isNumber(size.value) || size.value <= 0) return 0;
      return size.unit === 'sqft' ? size.value / SQFT_PER_M2 : size.value;
    }
    return 0;
  }

  function tierFor(area, tiers) {
    const tier = tiers.find((item) => item.max_m2 === null || area <= item.max_m2 + EPSILON);
    return tier ? tier.key : tiers[tiers.length - 1].key;
  }

  /* First part of a UK postcode (the district), or '' when it does not look like one. */
  function postcodeDistrict(text) {
    const clean = String(text || '')
      .toUpperCase()
      .replace(/[^A-Z0-9 ]/g, '')
      .trim();
    if (!clean) return '';

    let outward = clean.split(/\s+/)[0];
    if (!clean.includes(' ') && clean.length >= 5) outward = clean.slice(0, -3);

    return /^[A-Z]{1,2}[0-9][A-Z0-9]?$/.test(outward) ? outward : '';
  }

  function postcodeArea(district) {
    const match = /^[A-Z]{1,2}/.exec(district || '');
    return match ? match[0] : '';
  }

  function compartmentCost(area, bandKey, pricing) {
    const band = pricing.bands[bandKey];
    const tier = tierFor(area, pricing.size_tiers);
    return area * band.rate_per_m2[tier] + band.door[tier] + band.refrigeration_unit[tier];
  }

  function modifierAmount(modifier, base, area) {
    if (modifier.type === 'percent') return Math.max((base * modifier.value) / 100, modifier.min || 0);
    if (modifier.type === 'per_m2') return modifier.value * area;
    return modifier.value;
  }

  function bucketFor(value, buckets) {
    const bucket = buckets.find((item) => item.max === null || value <= item.max);
    return bucket ? bucket.key : '';
  }

  /*
   * Budget estimate (Module 1).
   * answers: { need, size, temp, site: { district, groundFloor, threePhase }, when }
   *   need: a key of pricing.products; size: { mode: 'dims', w, l, h } | { mode: 'area', value, unit } |
   *   { mode: 'run', metres }; temp: 'chiller' | 'freezer' | 'deep_freeze';
   *   threePhase: 'yes' | 'no' | 'unsure'; when: 'urgent' | '4_weeks' | '1_3_months' | 'planning'.
   * Returns null when the answers are not enough to price.
   */
  function estimateBudget(answers, config) {
    const pricing = config.pricing;
    const product = pricing.products[answers.need];
    if (!product) return null;

    let band = answers.temp || product.default_band;
    if (!pricing.bands[band]) band = product.default_band;

    const site = answers.site || {};
    let area = 0;
    let metres = 0;
    let base = 0;
    let modifierArea = 0;

    if (product.pricing === 'run') {
      metres = answers.size && isNumber(answers.size.metres) ? answers.size.metres : 0;
      if (metres <= 0) return null;
      metres = Math.max(metres, product.min_metres || 0);
      const rate = product.rate_per_metre[band] || product.rate_per_metre.chiller;
      base = metres * rate + (product.fixed || 0);
    } else {
      area = areaFromSize(answers.size);
      if (area <= 0) return null;

      if (product.pricing === 'combined') {
        const freezerBand = band === 'chiller' ? 'freezer' : band;
        const freezerArea = area * product.freezer_share;
        const chillerArea = area - freezerArea;
        base =
          (compartmentCost(chillerArea, 'chiller', pricing) + compartmentCost(freezerArea, freezerBand, pricing)) *
          (1 - (product.shared_wall_saving_percent || 0) / 100);
        band = freezerBand;
        modifierArea = freezerArea;
      } else {
        base = compartmentCost(area, band, pricing) * (product.multiplier || 1) + (product.fixed_extra || 0);
        modifierArea = area;
      }
    }

    /* Modifiers triggered by the answers. Percentages apply to the base price, not to each other. */
    const mods = pricing.modifiers;
    const triggered = [];

    function trigger(key, amountArea) {
      const modifier = mods[key];
      if (!modifier) return;
      triggered.push({ key, amount: modifierAmount(modifier, base, amountArea), highOnly: !!modifier.high_only });
    }

    function meetsArea(modifier) {
      return product.pricing === 'run' || !modifier.from_m2 || area >= modifier.from_m2 - EPSILON;
    }

    if (site.groundFloor === false) trigger('no_level_access', area);
    if (site.threePhase === 'no' && mods.no_three_phase && meetsArea(mods.no_three_phase)) trigger('no_three_phase', area);
    if (site.threePhase === 'unsure' && mods.three_phase_unsure && meetsArea(mods.three_phase_unsure)) {
      trigger('three_phase_unsure', area);
    }
    if (band === 'deep_freeze' && product.pricing !== 'run') trigger('deep_freeze_floor', modifierArea);
    if (answers.when === 'urgent') trigger('urgent', area);

    const district = postcodeDistrict(site.district);
    if (district && mods.london && mods.london.areas.includes(postcodeArea(district))) trigger('london', area);

    if (
      answers.size &&
      answers.size.mode === 'dims' &&
      isNumber(answers.size.h) &&
      mods.tall_room &&
      answers.size.h > mods.tall_room.over_height_m + EPSILON
    ) {
      trigger('tall_room', area);
    }

    const addLow = triggered.filter((item) => !item.highOnly).reduce((sum, item) => sum + item.amount, 0);
    const addHigh = triggered.reduce((sum, item) => sum + item.amount, 0);
    const spread = pricing.spread_percent / 100;
    const step = pricing.rounding;

    let low = roundDown((base + addLow) * (1 - spread), step);
    let high = roundUp((base + addHigh) * (1 + spread), step);
    let capped = false;

    low = Math.max(low, pricing.floor);
    if (high <= low) high = roundUp(low * (1 + 2 * spread), step);

    if (high > pricing.ceiling) {
      capped = true;
      high = pricing.ceiling;
      low = Math.min(low, roundDown(pricing.ceiling * (1 - 2 * spread), step));
    }

    const ordered = triggered.slice().sort((a, b) => b.amount - a.amount);
    const up = ordered.map((item) => mods[item.key].up).filter(Boolean).slice(0, 3);

    const down = ordered.map((item) => mods[item.key].down).filter(Boolean);
    (pricing.general_down || []).forEach((item) => {
      if (item.pricing && !item.pricing.includes(product.pricing)) return;
      if (item.bands && !item.bands.includes(band)) return;
      down.push(item.text);
    });

    return {
      low,
      high,
      capped,
      base: Math.round(base),
      band,
      area,
      metres,
      tier: product.pricing === 'run' ? '' : tierFor(area, pricing.size_tiers),
      includes: product.includes,
      quoteService: product.quote_service,
      up,
      down: down.slice(0, 3),
      modifiers: triggered.map((item) => item.key),
      bucket: bucketFor((low + high) / 2, pricing.range_buckets),
      placeholder: config.placeholder !== false,
    };
  }

  /*
   * Size recommendation (Module 2).
   * input: { product, quantity, frequency } with product a key of sizing.products and frequency
   * 'daily' | 'two_three' | 'weekly' (ignored for products counted as stock held, such as pallets).
   */
  function capacityOf(sizeArea, product, sizing) {
    const usable = (sizeArea / (1 + sizing.aisle_allowance_percent / 100)) * sizing.packing_factor;
    return Math.floor((usable * product.stack_height_m) / product.volume_m3 + EPSILON);
  }

  function palletCapacityOf(sizeArea, sizing) {
    const usable = (sizeArea / (1 + sizing.aisle_allowance_percent / 100)) * sizing.packing_factor;
    return Math.floor((usable * sizing.pallet_stack_height_m) / sizing.pallet_volume_m3 + EPSILON);
  }

  function recommendSize(input, config) {
    const sizing = config.sizing;
    const product = sizing.products[input.product];
    if (!product || !isNumber(input.quantity) || input.quantity <= 0) return null;

    const factor = product.basis === 'stored' ? 1 : sizing.holding_factor[input.frequency];
    if (!isNumber(factor)) return null;

    const held = input.quantity * factor;
    const storage = (held * product.volume_m3) / product.stack_height_m / sizing.packing_factor;
    const area = Math.max(storage * (1 + sizing.aisle_allowance_percent / 100), sizing.min_area_m2);
    const spread = sizing.area_range_percent / 100;
    const areaLow = Math.floor(area * (1 - spread) * 2 + EPSILON) / 2;
    const areaHigh = Math.ceil(area * (1 + spread) * 2 - EPSILON) / 2;

    const sizes = sizing.standard_sizes
      .map((size) => Object.assign({}, size, { area: size.w * size.l }))
      .sort((a, b) => a.area - b.area);

    let index = sizes.findIndex((size) => size.area >= area - EPSILON);
    const beyond = index === -1;
    if (beyond) index = sizes.length - 1;

    let start = index - 1;
    if (start < 0) start = 0;
    if (start + 3 > sizes.length) start = Math.max(0, sizes.length - 3);

    const shown = sizes.slice(start, start + 3).map((size) => ({
      id: size.id,
      w: size.w,
      l: size.l,
      area: size.area,
      image: size.image,
      alt: size.alt,
      recommended: !beyond && size.id === sizes[index].id,
      holds: capacityOf(size.area, product, sizing),
      pallets: palletCapacityOf(size.area, sizing),
    }));

    return {
      product: input.product,
      band: product.band,
      unit: product.unit,
      area,
      areaLow,
      areaHigh,
      held: Math.ceil(held - EPSILON),
      beyond,
      recommendedId: beyond ? '' : sizes[index].id,
      sizes: shown,
      storageNote: product.storage_note,
      placeholder: sizing.placeholder !== false,
    };
  }

  function sizeBucket(area) {
    if (area <= 5) return 'up_to_5_m2';
    if (area <= 10) return '5_to_10_m2';
    if (area <= 20) return '10_to_20_m2';
    if (area <= 35) return '20_to_35_m2';
    return 'over_35_m2';
  }

  /*
   * "Will it fit?" check. room: { w, l } internal size in metres; space: { w, l, h } clear floor and
   * ceiling height. The room needs its panels and a service gap on every side, and headroom for the
   * roof panel and the refrigeration unit above.
   */
  function checkFit(room, space, config) {
    const sizing = config.sizing;
    const fit = sizing.fit;
    const extra = fit.wall_allowance_m + fit.service_clearance_m;
    const needW = toTenths(room.w + extra);
    const needL = toTenths(room.l + extra);
    const overhead = fit.roof_panel_m + fit.unit_clearance_m;
    const needH = toTenths(fit.default_internal_height_m + overhead);
    const minH = toTenths(fit.min_internal_height_m + overhead);

    const spaceLong = Math.max(space.w, space.l);
    const spaceShort = Math.min(space.w, space.l);

    function footprintFits(w, l) {
      const long = Math.max(w + extra, l + extra);
      const short = Math.min(w + extra, l + extra);
      return long <= spaceLong + EPSILON && short <= spaceShort + EPSILON;
    }

    const footprintOk = footprintFits(room.w, room.l);
    const heightFull = isNumber(space.h) && space.h >= needH - EPSILON;
    const heightReduced = isNumber(space.h) && !heightFull && space.h >= minH - EPSILON;
    const fits = footprintOk && (heightFull || heightReduced);

    let internalHeight = fit.default_internal_height_m;
    if (heightReduced) internalHeight = Math.max(fit.min_internal_height_m, Math.floor((space.h - overhead) * 10 + EPSILON) / 10);

    let nextDown = null;
    let proportion = null;

    if (!fits && footprintOk === false) {
      const roomArea = room.w * room.l;
      const smaller = sizing.standard_sizes
        .map((size) => Object.assign({}, size, { area: size.w * size.l }))
        .filter((size) => size.area < roomArea - EPSILON && footprintFits(size.w, size.l))
        .sort((a, b) => b.area - a.area);
      if (smaller.length) nextDown = { id: smaller[0].id, w: smaller[0].w, l: smaller[0].l, area: smaller[0].area };

      const width = toTenths(spaceShort - extra);
      if (width >= 1.2) {
        const length = Math.ceil((roomArea / width) * 10 - EPSILON) / 10;
        if (length + extra <= spaceLong + EPSILON) proportion = { w: Math.max(length, width), l: Math.min(length, width) };
      }
    }

    return {
      fits,
      reason: !footprintOk ? 'footprint' : heightFull ? 'ok' : heightReduced ? 'height_reduced' : 'height',
      needW: Math.max(needW, needL),
      needL: Math.min(needW, needL),
      needH,
      minH,
      internalHeight,
      doorClearance: fit.door_clearance_m,
      nextDown,
      proportion,
    };
  }

  /* Finance (Module 4): an illustrative monthly range from a total range and a term. */
  function monthlyPayment(amount, annualRatePercent, months) {
    const rate = annualRatePercent / 100 / 12;
    if (rate === 0) return amount / months;
    return (amount * rate) / (1 - Math.pow(1 + rate, -months));
  }

  function financeRange(lowAmount, highAmount, months, config) {
    const finance = config.finance;
    if (!isNumber(lowAmount) || !isNumber(highAmount) || lowAmount <= 0 || !isNumber(months) || months <= 0) return null;
    const low = roundDown(monthlyPayment(lowAmount, finance.annual_rate_low_percent, months), finance.rounding);
    const high = roundUp(monthlyPayment(highAmount, finance.annual_rate_high_percent, months), finance.rounding);
    return { low, high: Math.max(high, low + finance.rounding), months, placeholder: finance.placeholder !== false };
  }

  /* Office hours, in the office's own time zone whatever the visitor's clock says. */
  const WEEKDAYS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
  const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  function officeClock(date, timezone) {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .forEach((part) => {
        parts[part.type] = part.value;
      });

    return {
      weekday: WEEKDAYS[parts.weekday],
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      minutes: Number(parts.hour) * 60 + Number(parts.minute),
    };
  }

  function minutesOf(time) {
    const [hours, minutes] = String(time).split(':').map(Number);
    return hours * 60 + (minutes || 0);
  }

  function dateKey(clock) {
    return `${clock.year}-${String(clock.month).padStart(2, '0')}-${String(clock.day).padStart(2, '0')}`;
  }

  function isWorkingDay(weekday, key, hours, holidays) {
    return hours.days.includes(weekday) && !(holidays || []).includes(key);
  }

  /* holidays: 'YYYY-MM-DD' dates (bank holidays) when the office is closed all day. */
  function isOfficeOpen(date, hours, holidays) {
    const clock = officeClock(date, hours.timezone);
    return (
      isWorkingDay(clock.weekday, dateKey(clock), hours, holidays) &&
      clock.minutes >= minutesOf(hours.open) &&
      clock.minutes < minutesOf(hours.close)
    );
  }

  /*
   * The day we can call back at the given times: the next working day. Today counts only when it is a
   * working day, the rule allows it (same_day_if_before_opening) and the earliest time is at least
   * min_notice_minutes away. Days outside hours.days and the bank holidays are skipped.
   */
  function callbackDay(date, hours, times, options) {
    const opts = options || {};
    const holidays = opts.holidays || [];
    const notice = Number.isFinite(opts.minNoticeMinutes) ? opts.minNoticeMinutes : 30;
    const sameDay = opts.sameDay !== false;
    const clock = officeClock(date, hours.timezone);
    const earliest = Math.min.apply(null, times.map(minutesOf));
    const today = Date.UTC(clock.year, clock.month - 1, clock.day);

    function dayAt(offset) {
      const day = new Date(today + offset * 86400000);
      const key = `${day.getUTCFullYear()}-${String(day.getUTCMonth() + 1).padStart(2, '0')}-${String(day.getUTCDate()).padStart(2, '0')}`;
      return { day, key, weekday: ((clock.weekday - 1 + offset) % 7) + 1 };
    }

    let offset = 1;
    const now = dayAt(0);
    if (sameDay && isWorkingDay(now.weekday, now.key, hours, holidays) && clock.minutes + notice <= earliest) offset = 0;
    for (let guard = 0; guard < 21; guard += 1) {
      const candidate = dayAt(offset);
      if (isWorkingDay(candidate.weekday, candidate.key, hours, holidays)) break;
      offset += 1;
    }

    const chosen = dayAt(offset);
    const dateLabel = `${DAY_NAMES[chosen.weekday]} ${chosen.day.getUTCDate()} ${MONTH_NAMES[chosen.day.getUTCMonth()]}`;
    const relative = offset === 0 ? 'today' : offset === 1 ? 'tomorrow' : `on ${DAY_NAMES[chosen.weekday]}`;

    return { offset, weekday: chosen.weekday, dayName: DAY_NAMES[chosen.weekday], dateLabel, relative, date: chosen.key };
  }

  /* Call-back times inside opening hours: never before opening, never too close to closing. */
  function validCallbackTimes(times, hours, rule) {
    const settings = rule || {};
    const first = minutesOf(hours.open) + (settings.earliest_after_opening_minutes || 0);
    const last = minutesOf(hours.close) - (settings.latest_before_closing_minutes || 0);
    const valid = (times || []).filter((time) => /^\d{1,2}:\d{2}$/.test(time) && minutesOf(time) >= first && minutesOf(time) <= last);
    return valid.length ? valid : [hours.open];
  }

  /*
   * Everything the interface needs for "Choose a time for us to call you", read from the config's
   * contact block: kind 'estimate' uses callback_slots, kind 'repair' uses repair_callback_time.
   */
  function callbackOffer(date, contact, kind) {
    const hours = contact.office_hours;
    const rule = contact.callback_rule || {};
    const wanted = kind === 'repair' ? [contact.repair_callback_time] : contact.callback_slots;
    const times = validCallbackTimes(wanted, hours, rule);
    const day = callbackDay(date, hours, times, {
      holidays: contact.bank_holidays,
      minNoticeMinutes: rule.min_notice_minutes,
      sameDay: rule.same_day_if_before_opening,
    });
    return { day, times };
  }

  function formatMoney(value) {
    return `£${Math.round(value).toLocaleString('en-GB')}`;
  }

  function formatRange(low, high) {
    return `${formatMoney(low)} to ${formatMoney(high)}`;
  }

  function formatMetres(value) {
    return `${Number(value).toFixed(1)}`;
  }

  const engine = {
    SQFT_PER_M2,
    areaFromSize,
    tierFor,
    postcodeDistrict,
    postcodeArea,
    estimateBudget,
    bucketFor,
    recommendSize,
    capacityOf,
    sizeBucket,
    checkFit,
    monthlyPayment,
    financeRange,
    officeClock,
    isOfficeOpen,
    callbackDay,
    validCallbackTimes,
    callbackOffer,
    formatMoney,
    formatRange,
    formatMetres,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
    return;
  }

  root.AlaskaColdEstimatorEngine = engine;
  if (typeof document === 'undefined') return;

  /* ------------------------------------------------------------------ */
  /* Interface                                                            */
  /* ------------------------------------------------------------------ */

  const STORAGE_KEY = 'ac-estimator';
  const MODULE_HASHES = { '#estimate': 'estimate', '#sizing': 'sizing', '#repair': 'repair', '#finance': 'finance', '#ask': 'ask' };
  const FIRST_SCREEN = { estimate: 'estimate:need', sizing: 'sizing:product', repair: 'repair:fault', finance: 'finance:main', ask: 'ask:main' };
  const KNOWLEDGE_KEY = 'ac-knowledge';
  const UNMATCHED_KEY = 'ac-ask-unmatched';

  /* Line icons, 24 x 24, drawn for this site. */
  const ICONS = {
    estimate: '<path d="M16.5 19.5H7.5c1.6-1.4 2-3 2-4.6V9.5a3.8 3.8 0 0 1 6.6-2.5"/><path d="M6.5 13.5h7"/>',
    sizing: '<path d="M3.5 16.5 16.5 3.5l4 4-13 13z"/><path d="M7 13l2 2M10 10l2 2M13 7l2 2"/>',
    repair: '<path d="M14.7 6.3a4 4 0 0 0-5.3 5.3L3.5 17.5l3 3 5.9-5.9a4 4 0 0 0 5.3-5.3l-2.5 2.5-2.6-.4-.4-2.6z"/>',
    finance: '<rect x="3.5" y="5" width="17" height="15.5" rx="2"/><path d="M3.5 9.5h17M8 3v4M16 3v4M8 14h3M8 17h6"/>',
    ask: '<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 4v-4h0A2.5 2.5 0 0 1 4 13.5z"/><path d="M10 8a2 2 0 1 1 2.6 1.9c-.4.2-.6.5-.6.9v.4M12 13.2h.01"/>',
    send: '<path d="M4 12l16-8-6 16-2.5-6.5z"/><path d="M11.5 13.5 20 4"/>',
    meat: '<path d="M14.5 3.5c3.6 0 6 2.6 6 5.8 0 4.6-5.2 9.2-10.5 9.2-3.1 0-5.5-1.9-5.5-4.5 0-2.4 1.8-3.3 3.6-4.6 2-1.5 2.4-5.9 6.4-5.9z"/><path d="M5.6 17.4 3.5 19.5"/><circle cx="14" cy="9.5" r="1.8"/>',
    fish: '<path d="M2.5 12c2.6-3.6 6.6-5.5 10.6-4.6 2.2.5 3.9 1.8 5.4 3.4l3-2.3v7l-3-2.3c-1.5 1.6-3.2 2.9-5.4 3.4-4 .9-8-1-10.6-4.6z"/><circle cx="7" cy="11.2" r=".9"/>',
    produce: '<path d="M12 8.2c-1.6-1.6-6.5-1.8-6.5 3.6 0 4.2 2.8 8.7 5 8.7.8 0 1-.4 1.5-.4s.7.4 1.5.4c2.2 0 5-4.5 5-8.7 0-5.4-4.9-5.2-6.5-3.6z"/><path d="M12 8c0-2 1-3.6 3-4.5"/>',
    dairy: '<path d="M9 2.5h6v3l2 3v12a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-12l2-3z"/><path d="M7 12.5h10"/>',
    frozen: '<path d="M12 2.5v19M3.8 7.2l16.4 9.6M3.8 16.8l16.4-9.6"/><path d="m9.5 4 2.5 2 2.5-2M9.5 20l2.5-2 2.5 2"/>',
    bakery: '<path d="M4 11a4.5 4.5 0 0 1 4.5-4.5h7A4.5 4.5 0 0 1 20 11c0 1.4-.8 2.1-1.5 2.4v5.1a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-5.1C4.8 13.1 4 12.4 4 11z"/><path d="M9 9.5v3M12 9.5v3M15 9.5v3"/>',
    drinks: '<path d="M8 2.5h8l1 3.5v13.5a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6z"/><path d="M7 7h10M7 17h10"/>',
    pharma: '<path d="M10.6 3.9a5 5 0 0 1 7.1 7.1l-6.7 6.7a5 5 0 0 1-7.1-7.1z"/><path d="m7.2 7.3 6.9 6.9"/>',
    mixed: '<path d="M3.5 12.5h8v8h-8zM12.5 12.5h8v8h-8zM8 3.5h8v8H8z"/>',
    chevron: '<path d="m9 6 6 6-6 6"/>',
    phone:
      '<path d="M21 16.4v3a2 2 0 0 1-2.2 2 19.6 19.6 0 0 1-8.5-3 19.3 19.3 0 0 1-6-6 19.6 19.6 0 0 1-3-8.6A2 2 0 0 1 3.3 1.7h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L7.3 9.4a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.6 2z"/>',
    whatsapp:
      '<path d="M3.5 20.5l1.3-4.2A8.5 8.5 0 1 1 8 19.4z"/><path d="M9 8.5c0 3.3 2.9 6.5 6.5 6.5l1-1.5-2-1-1 1c-1.2-.5-2.5-1.8-3-3l1-1-1-2z"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5.5M12 7.5h.01"/>',
    check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
    cross: '<path d="M6 6l12 12M18 6L6 18"/>',
  };

  function icon(name, size) {
    const px = size || 24;
    return `<svg class="ac-est-icon" width="${px}" height="${px}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[name] || ''}</svg>`;
  }

  function escapeHtml(value) {
    return String(value === undefined || value === null ? '' : value).replace(
      /[&<>"']/g,
      (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
    );
  }

  function parseNumber(value) {
    const number = parseFloat(String(value === undefined || value === null ? '' : value).replace(',', '.').trim());
    return Number.isFinite(number) ? number : NaN;
  }

  function trimNumber(value) {
    return Number.isFinite(value) ? String(Math.round(value * 100) / 100) : '';
  }

  /* Session storage can throw in private windows or when blocked, so every call is guarded. */
  const storage = {
    read() {
      try {
        return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      } catch (error) {
        return null;
      }
    },
    write(value) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch (error) {
        /* answers are simply not kept across pages */
      }
    },
  };

  function siteApi() {
    return window.AlaskaCold || null;
  }

  function track(eventName, data) {
    const api = siteApi();
    if (api && typeof api.track === 'function') {
      api.track(eventName, data || {});
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data || {}));
  }

  function freshState() {
    return {
      screen: 'home',
      history: [],
      estimate: { sizeMode: 'dims', h: 2.2, areaUnit: 'm2' },
      sizing: {},
      repair: {},
      finance: {},
      ask: { thread: [] },
    };
  }

  function restoredState() {
    const saved = storage.read();
    const state = freshState();
    if (!saved || typeof saved !== 'object') return state;
    ['estimate', 'sizing', 'repair', 'finance', 'ask'].forEach((key) => {
      if (saved[key] && typeof saved[key] === 'object') Object.assign(state[key], saved[key]);
    });
    if (!Array.isArray(state.ask.thread)) state.ask.thread = [];
    if (typeof saved.screen === 'string') state.screen = saved.screen;
    if (Array.isArray(saved.history)) state.history = saved.history.filter((item) => typeof item === 'string').slice(-20);
    return state;
  }

  function init() {
    const dialog = document.getElementById('ac-estimator');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const body = dialog.querySelector('[data-ac-estimator-body]');
    const progress = dialog.querySelector('[data-ac-estimator-progress]');
    const backButton = dialog.querySelector('[data-ac-estimator-back]');
    const settings = dialog.dataset;
    const contact = {
      phone: settings.phone || '+44 7879 685742',
      phoneHref: settings.phoneHref || 'tel:+447879685742',
      whatsapp: settings.whatsapp || '447879685742',
    };
    const showFinance = settings.showFinance === 'true';

    let config = null;
    let configRequest = null;
    let state = restoredState();
    let returnFocusTo = null;
    let lastResult = '';

    /* Config */

    function loadConfig() {
      if (config) return Promise.resolve(config);
      if (configRequest) return configRequest;

      const inline = dialog.querySelector('[data-ac-estimator-config]');
      if (inline && inline.textContent.trim()) {
        try {
          config = JSON.parse(inline.textContent);
          return Promise.resolve(config);
        } catch (error) {
          /* fall back to the file */
        }
      }

      configRequest = fetch(settings.configUrl, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Estimator config: ${response.status}`);
          return response.json();
        })
        .then((json) => {
          config = json;
          return json;
        })
        .catch((error) => {
          configRequest = null;
          throw error;
        });
      return configRequest;
    }

    function save() {
      storage.write(state);
    }

    /* Shared pieces of markup */

    function heading(text, lead) {
      return (
        `<h2 class="ac-est-heading" tabindex="-1" data-ac-est-heading>${escapeHtml(text)}</h2>` +
        (lead ? `<p class="ac-est-lead">${escapeHtml(lead)}</p>` : '')
      );
    }

    function optionList(field, options, current, columns) {
      return (
        `<div class="ac-est-options${columns ? ' ac-est-options--two' : ''}">` +
        options
          .map(
            (option) =>
              `<button type="button" class="ac-est-option" data-action="choose" data-field="${field}" data-value="${escapeHtml(option.value)}" aria-pressed="${String(current === option.value)}">` +
              (option.icon ? `<span class="ac-est-option__icon">${icon(option.icon, 22)}</span>` : '') +
              `<span class="ac-est-option__text"><span class="ac-est-option__title">${escapeHtml(option.title)}</span>` +
              (option.text ? `<span class="ac-est-option__desc">${escapeHtml(option.text)}</span>` : '') +
              `</span><span class="ac-est-option__go">${icon('chevron', 20)}</span></button>`
          )
          .join('') +
        '</div>'
      );
    }

    function segment(name, legend, options, current) {
      return (
        `<fieldset class="ac-est-segment"><legend class="ac-est-legend">${escapeHtml(legend)}</legend><div class="ac-est-segment__options">` +
        options
          .map(
            (option) =>
              `<label class="ac-est-segment__option"><input type="radio" name="${name}" value="${escapeHtml(option.value)}"${
                current === option.value ? ' checked' : ''
              }><span>${escapeHtml(option.title)}</span></label>`
          )
          .join('') +
        '</div></fieldset>'
      );
    }

    function textField(id, name, label, value, options) {
      const opts = options || {};
      const hintId = opts.hint ? `${id}-hint` : '';
      return (
        `<div class="ac-est-field${opts.className ? ` ${opts.className}` : ''}">` +
        `<label class="ac-est-field__label" for="${id}">${escapeHtml(label)}${
          opts.unit ? ` <span class="ac-est-field__unit">(${escapeHtml(opts.unit)})</span>` : ''
        }</label>` +
        (opts.hint ? `<p class="ac-est-field__hint" id="${hintId}">${escapeHtml(opts.hint)}</p>` : '') +
        `<input class="ac-est-field__input" id="${id}" name="${name}" type="text" inputmode="${opts.inputmode || 'decimal'}" autocomplete="off" spellcheck="false" value="${escapeHtml(value)}"${
          hintId ? ` aria-describedby="${hintId}"` : ''
        }${opts.maxlength ? ` maxlength="${opts.maxlength}"` : ''}${opts.autocapitalize ? ` autocapitalize="${opts.autocapitalize}"` : ''}>` +
        '</div>'
      );
    }

    function errorLine() {
      return '<p class="ac-est-error" role="alert" data-ac-est-error></p>';
    }

    function continueButton(label) {
      return `<div class="ac-est-next" data-ac-est-next><button type="submit" class="ac-btn ac-btn--primary ac-btn--lg">${escapeHtml(label || 'Continue')}</button></div>`;
    }

    function callLine(module) {
      return `<p class="ac-est-talk">${icon('phone', 18)}<span>Prefer to talk? Call <a href="${escapeHtml(contact.phoneHref)}" data-ac-est-module="${module || ''}">${escapeHtml(contact.phone)}</a></span></p>`;
    }

    function showError(form, message) {
      const line = form.querySelector('[data-ac-est-error]');
      if (line) line.textContent = message;
    }

    function officeOpen() {
      return engine.isOfficeOpen(new Date(), config.contact.office_hours, config.contact.bank_holidays);
    }

    /* Hand-off to the quote form. The quote form is the only place contact details are asked for. */
    function toQuote(trigger, detail) {
      save();
      track('estimator_to_quote', { module: detail.sourceModule, action: detail.action });

      const api = siteApi();
      if (api && typeof api.openQuote === 'function') {
        api.openQuote(Object.assign({ trigger }, detail));
        return;
      }

      /* Without the quote form API, a plain quote button does the job with the service pre-filled. */
      const button = document.createElement('button');
      button.type = 'button';
      button.hidden = true;
      button.dataset.quoteOpen = '';
      button.dataset.quoteSource = detail.source;
      if (detail.service) button.dataset.quoteService = detail.service;
      dialog.appendChild(button);
      button.click();
      button.remove();
    }

    /* Screens. Each one: module, step and number of steps (for the dots), render() and optional mount(). */
    const SCREENS = {};
    const FORMS = {};
    const ACTIONS = {};

    /* Home: the four cards */

    const MODULE_CARDS = [
      {
        module: 'estimate',
        icon: 'estimate',
        title: 'Budget estimate',
        text: 'A price range for a new cold room, freezer room or display refrigeration in about 60 seconds.',
      },
      {
        module: 'sizing',
        icon: 'sizing',
        title: 'What size do I need',
        text: 'Work out the room size from what you store, and check it fits your space.',
      },
      {
        module: 'repair',
        icon: 'repair',
        title: 'My cold room has a problem',
        text: 'What to do right now to protect your stock, and the fastest way to reach an engineer.',
      },
      {
        module: 'finance',
        icon: 'finance',
        title: 'Spread the cost',
        text: 'An illustrative monthly figure over 12 to 48 months.',
      },
      {
        module: 'ask',
        icon: 'ask',
        title: 'Ask a question',
        text: 'Type a question and get an answer from our own pages, or pass it to the team.',
      },
    ];

    SCREENS.home = {
      module: '',
      render() {
        const cards = MODULE_CARDS.filter(
          (card) => SCREENS[FIRST_SCREEN[card.module]] && (card.module !== 'finance' || showFinance)
        );
        return (
          heading('How can we help?', 'Free and anonymous: no contact details needed. Every figure is a range, subject to a free site survey.') +
          '<div class="ac-est-cards">' +
          cards
            .map(
              (card) =>
                `<button type="button" class="ac-est-card" data-action="module" data-value="${card.module}">` +
                `<span class="ac-est-card__icon">${icon(card.icon, 24)}</span>` +
                `<span class="ac-est-card__title">${escapeHtml(card.title)}</span>` +
                `<span class="ac-est-card__text">${escapeHtml(card.text)}</span></button>`
            )
            .join('') +
          '</div>' +
          callLine('')
        );
      },
    };

    /* Module 1: budget estimate */

    const NEED_TEXT = {
      cold_room: 'A chilled walk-in room, 0 to +5 °C',
      freezer_room: 'A walk-in freezer or blast freezer',
      combined: 'A chiller and a freezer side by side',
      cold_storage_plant: 'Cold stores for pallets and bulk stock',
      display: 'Multidecks, serve over counters and glass-door freezers',
    };

    const TEMP_OPTIONS = {
      chiller: { title: 'Chiller', text: '0 to +5 °C: meat, dairy, fruit, veg, drinks' },
      freezer: { title: 'Freezer', text: '-18 to -25 °C: frozen food' },
      deep_freeze: { title: 'Deep freeze', text: 'Down to -40 °C: blast freezing and long-term storage' },
      not_sure: { title: 'Not sure', text: 'Tell us what you will store and we will choose' },
    };

    const WHEN_OPTIONS = [
      { value: 'urgent', title: 'Urgent', text: 'As soon as possible' },
      { value: '4_weeks', title: 'Within 4 weeks' },
      { value: '1_3_months', title: 'In 1 to 3 months' },
      { value: 'planning', title: 'Just planning', text: 'No date yet' },
    ];

    const WHEN_TEXT = { urgent: 'urgent', '4_weeks': 'within 4 weeks', '1_3_months': 'in 1 to 3 months', planning: 'just planning' };
    const DIM_PRESETS = [
      [2, 2],
      [3, 3],
      [4, 3],
      [5, 4],
      [6, 5],
    ];
    const RUN_PRESETS = [2.5, 3.75, 5, 7.5, 10];

    function estimateProduct() {
      return config.pricing.products[state.estimate.need] || null;
    }

    function estimateAnswers() {
      const answers = state.estimate;
      const product = estimateProduct();
      let size = null;
      if (product && product.pricing === 'run') size = { mode: 'run', metres: answers.metres };
      else if (answers.sizeMode === 'area') size = { mode: 'area', value: answers.area, unit: answers.areaUnit };
      else size = { mode: 'dims', w: answers.w, l: answers.l, h: answers.h };

      return {
        need: answers.need,
        size,
        temp: answers.temp,
        site: {
          district: answers.district,
          groundFloor: answers.ground === 'yes' ? true : answers.ground === 'no' ? false : undefined,
          threePhase: answers.power,
        },
        when: answers.when,
      };
    }

    function lowerFirst(text) {
      return text ? `${text.charAt(0).toLowerCase()}${text.slice(1)}` : '';
    }

    function bandLabel(band) {
      const entry = config.pricing.bands[band];
      return entry ? entry.label : '';
    }

    function sizeText() {
      const answers = state.estimate;
      const product = estimateProduct();
      if (product && product.pricing === 'run') return `${trimNumber(answers.metres)} m of display cabinets`;
      if (answers.sizeMode === 'area') {
        const m2 = engine.areaFromSize({ mode: 'area', value: answers.area, unit: answers.areaUnit });
        return answers.areaUnit === 'sqft'
          ? `${trimNumber(answers.area)} sq ft (${m2.toFixed(1)} m²) of floor`
          : `${trimNumber(answers.area)} m² of floor`;
      }
      return `${engine.formatMetres(answers.w)} × ${engine.formatMetres(answers.l)} × ${engine.formatMetres(answers.h)} m inside (${(answers.w * answers.l).toFixed(1)} m²)`;
    }

    /* Short size for the summary above the quote form. */
    function shortSizeText() {
      const answers = state.estimate;
      const product = estimateProduct();
      if (product && product.pricing === 'run') return `${trimNumber(answers.metres)} m of cabinets`;
      if (answers.sizeMode === 'area') {
        return `${engine.areaFromSize({ mode: 'area', value: answers.area, unit: answers.areaUnit }).toFixed(1)} m²`;
      }
      return `${engine.formatMetres(answers.w)} × ${engine.formatMetres(answers.l)} m`;
    }

    function temperatureText(result) {
      if (state.estimate.need === 'combined') return `chiller 0 to +5 °C and ${lowerFirst(bandLabel(result.band))}`;
      return bandLabel(result.band);
    }

    SCREENS['estimate:need'] = {
      module: 'estimate',
      step: 1,
      steps: 5,
      render() {
        const options = Object.keys(config.pricing.products).map((key) => ({
          value: key,
          title: config.pricing.products[key].label,
          text: NEED_TEXT[key],
        }));
        return heading('What do you need?') + optionList('need', options, state.estimate.need);
      },
    };

    SCREENS['estimate:size'] = {
      module: 'estimate',
      step: 2,
      steps: 5,
      render() {
        const answers = state.estimate;
        const product = estimateProduct();

        if (product && product.pricing === 'run') {
          return (
            heading('How much display do you need?', 'Add up the length of the cabinets you want, in metres.') +
            '<form class="ac-est-form" data-form="size-run" novalidate>' +
            '<p class="ac-est-sublabel" id="ac-est-run-presets">Common lengths</p>' +
            '<div class="ac-est-presets" role="group" aria-labelledby="ac-est-run-presets">' +
            RUN_PRESETS.map(
              (metres) => `<button type="button" class="ac-est-chip" data-action="preset-run" data-value="${metres}">${metres} m</button>`
            ).join('') +
            '</div>' +
            '<div class="ac-est-fields">' +
            textField('ac-est-metres', 'metres', 'Length of cabinets', trimNumber(answers.metres), { unit: 'metres' }) +
            '</div>' +
            errorLine() +
            continueButton() +
            '</form>'
          );
        }

        const mode = answers.sizeMode || 'dims';
        return (
          heading('How big?', 'Choose one way to answer. Inside measurements are best.') +
          '<form class="ac-est-form" data-form="size" novalidate>' +
          segment(
            'size-mode',
            'How do you want to give the size?',
            [
              { value: 'dims', title: 'Dimensions' },
              { value: 'area', title: 'Floor area' },
              { value: 'store', title: 'I know what I store' },
            ],
            mode
          ) +
          `<div class="ac-est-panel" data-panel="dims"${mode === 'dims' ? '' : ' hidden'}>` +
          '<p class="ac-est-sublabel" id="ac-est-dim-presets">Common sizes, width × length</p>' +
          '<div class="ac-est-presets" role="group" aria-labelledby="ac-est-dim-presets">' +
          DIM_PRESETS.map(
            ([w, l]) => `<button type="button" class="ac-est-chip" data-action="preset-dims" data-w="${w}" data-l="${l}">${w} × ${l} m</button>`
          ).join('') +
          '</div>' +
          '<div class="ac-est-fields ac-est-fields--three">' +
          textField('ac-est-w', 'w', 'Width', trimNumber(answers.w), { unit: 'm' }) +
          textField('ac-est-l', 'l', 'Length', trimNumber(answers.l), { unit: 'm' }) +
          textField('ac-est-h', 'h', 'Height', trimNumber(answers.h), { unit: 'm', hint: 'Most rooms are 2.2 m inside.' }) +
          '</div></div>' +
          `<div class="ac-est-panel" data-panel="area"${mode === 'area' ? '' : ' hidden'}>` +
          '<div class="ac-est-fields ac-est-fields--area">' +
          textField('ac-est-area', 'area', 'Floor area', trimNumber(answers.area)) +
          segment(
            'area-unit',
            'Unit',
            [
              { value: 'm2', title: 'm²' },
              { value: 'sqft', title: 'sq ft' },
            ],
            answers.areaUnit || 'm2'
          ) +
          '</div></div>' +
          `<div class="ac-est-panel" data-panel="store"${mode === 'store' ? '' : ' hidden'}>` +
          '<p class="ac-est-text">Tell us what you store, how much and how often it arrives. We will work out the size, then bring you back here.</p>' +
          '<button type="button" class="ac-btn ac-btn--secondary" data-action="to-sizing">Work out my size</button>' +
          '</div>' +
          errorLine() +
          `<div class="ac-est-next" data-ac-est-next${mode === 'store' ? ' hidden' : ''}><button type="submit" class="ac-btn ac-btn--primary ac-btn--lg">Continue</button></div>` +
          '</form>'
        );
      },
    };

    SCREENS['estimate:temp'] = {
      module: 'estimate',
      step: 3,
      steps: 5,
      render() {
        const need = state.estimate.need;
        let keys = ['chiller', 'freezer', 'deep_freeze', 'not_sure'];
        let title = 'What temperature?';
        let lead = '';
        if (need === 'combined') {
          keys = ['freezer', 'deep_freeze', 'not_sure'];
          title = 'What temperature for the freezer side?';
          lead = 'The chiller side runs at 0 to +5 °C.';
        } else if (need === 'display') {
          keys = ['chiller', 'freezer', 'not_sure'];
        }
        const options = keys.map((key) => Object.assign({ value: key }, TEMP_OPTIONS[key]));
        const current = state.estimate.tempFrom ? 'not_sure' : state.estimate.temp;
        return heading(title, lead) + optionList('temp', options, current);
      },
    };

    SCREENS['estimate:store'] = {
      module: 'estimate',
      step: 3,
      steps: 5,
      render() {
        const products = config.sizing.products;
        const options = Object.keys(products).map((key) => ({ value: key, title: products[key].label, icon: products[key].icon }));
        return heading('What will you store?', 'We will choose the temperature for you.') + optionList('store', options, state.estimate.tempFrom, true);
      },
    };

    SCREENS['estimate:site'] = {
      module: 'estimate',
      step: 4,
      steps: 5,
      render() {
        const answers = state.estimate;
        return (
          heading('Where is the site?') +
          '<form class="ac-est-form" data-form="site" novalidate>' +
          textField('ac-est-district', 'district', 'Postcode district', answers.district || '', {
            hint: 'First part only, for example SE1 or CM2. We use it for travel and access, nothing else.',
            inputmode: 'text',
            maxlength: 8,
            autocapitalize: 'characters',
            className: 'ac-est-field--short',
          }) +
          segment(
            'ground',
            'Ground floor with level access?',
            [
              { value: 'yes', title: 'Yes' },
              { value: 'no', title: 'No' },
            ],
            answers.ground
          ) +
          segment(
            'power',
            'Existing three-phase power?',
            [
              { value: 'yes', title: 'Yes' },
              { value: 'no', title: 'No' },
              { value: 'unsure', title: 'Unsure' },
            ],
            answers.power
          ) +
          errorLine() +
          continueButton() +
          '</form>'
        );
      },
    };

    SCREENS['estimate:when'] = {
      module: 'estimate',
      step: 5,
      steps: 5,
      render() {
        return heading('When do you need it?') + optionList('when', WHEN_OPTIONS, state.estimate.when);
      },
    };

    function callbackBlock(module) {
      const offer = engine.callbackOffer(new Date(), config.contact, 'estimate');
      const day = offer.day;
      const times = offer.times;
      return (
        '<div class="ac-est-callback">' +
        `<p class="ac-est-callback__title">We are closed now. Choose a time for us to call you ${escapeHtml(day.relative)}.</p>` +
        `<p class="ac-est-callback__day">${escapeHtml(day.dateLabel)}</p>` +
        '<div class="ac-est-callback__slots">' +
        times
          .map(
            (time) =>
              `<button type="button" class="ac-btn ac-btn--secondary ac-btn--sm" data-action="callback" data-module="${module}" data-value="${escapeHtml(
                `${time}, ${day.dateLabel}`
              )}" aria-label="Call me at ${escapeHtml(time)} ${escapeHtml(day.relative)}, ${escapeHtml(day.dateLabel)}">${escapeHtml(time)}</button>`
          )
          .join('') +
        '</div></div>'
      );
    }

    function estimateResult() {
      return engine.estimateBudget(estimateAnswers(), config);
    }

    SCREENS['estimate:result'] = {
      module: 'estimate',
      step: 5,
      steps: 5,
      result: true,
      render() {
        const result = estimateResult();
        if (!result) {
          return (
            heading('We need a little more', 'Some answers are missing. Go back and check the size and the site.') +
            '<button type="button" class="ac-btn ac-btn--secondary" data-action="restart" data-value="estimate">Start again</button>'
          );
        }

        const product = estimateProduct();
        const open = officeOpen();
        const listOr = (items, empty) =>
          items.length ? `<ul class="ac-est-list">${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : `<p>${escapeHtml(empty)}</p>`;

        return (
          '<div class="ac-est-result" role="group" aria-labelledby="ac-estimator-heading">' +
          `<h2 class="ac-est-result__range" tabindex="-1" data-ac-est-heading>Estimated budget: ${escapeHtml(
            engine.formatRange(result.low, result.high)
          )}, subject to survey</h2>` +
          `<p class="ac-est-result__meta">${escapeHtml(product.label)} · ${escapeHtml(sizeText())} · ${escapeHtml(temperatureText(result))} · ${escapeHtml(
            config.vat_note || 'excluding VAT'
          )}</p>` +
          (result.capped ? '<p class="ac-est-result__note">Large projects can cost more than this. We price them after a survey.</p>' : '') +
          (result.placeholder ? `<p class="ac-est-flag">${icon('info', 18)}<span>Estimates are illustrative until pricing is confirmed.</span></p>` : '') +
          '</div>' +
          '<dl class="ac-est-lines">' +
          `<div class="ac-est-lines__row"><dt>What the range includes</dt><dd><p>${escapeHtml(result.includes)}</p></dd></div>` +
          `<div class="ac-est-lines__row"><dt>What moves it up</dt><dd>${listOr(result.up, 'Nothing in your answers adds to the price.')}</dd></div>` +
          `<div class="ac-est-lines__row"><dt>What moves it down</dt><dd>${listOr(result.down, 'Your answers already keep the price down.')}</dd></div>` +
          '</dl>' +
          '<div class="ac-est-actions">' +
          '<button type="button" class="ac-btn ac-btn--quote ac-btn--lg" data-action="estimate-quote" data-value="survey">Book a free site survey</button>' +
          '<button type="button" class="ac-btn ac-btn--secondary ac-btn--lg" data-action="estimate-quote" data-value="email">Email me this estimate</button>' +
          '</div>' +
          callLine('estimate') +
          (open ? '' : callbackBlock('estimate')) +
          '<p class="ac-est-restart"><button type="button" class="ac-est-textbutton" data-action="restart" data-value="estimate">Start a new estimate</button></p>'
        );
      },
      onResult() {
        const result = estimateResult();
        if (!result) return;
        const key = `estimate:${result.low}:${result.high}`;
        if (key === lastResult) return;
        lastResult = key;
        track('estimator_result', { module: 'estimate', range_bucket: result.bucket, out_of_hours: !officeOpen() });
      },
    };

    function estimateInputs(result) {
      const answers = state.estimate;
      const inputs = { m: 'estimate', need: answers.need, temp: result.band, d: engine.postcodeDistrict(answers.district) };
      const product = estimateProduct();
      if (product.pricing === 'run') inputs.run = answers.metres;
      else if (answers.sizeMode === 'area') Object.assign(inputs, { area: answers.area, unit: answers.areaUnit });
      else Object.assign(inputs, { w: answers.w, l: answers.l, h: answers.h });
      Object.assign(inputs, { ground: answers.ground, power: answers.power, when: answers.when });
      return JSON.stringify(inputs);
    }

    function estimateMessage(result) {
      const answers = state.estimate;
      const product = estimateProduct();
      return (
        `Budget estimate: ${product.label}, ${sizeText()}, ${temperatureText(result)}. ` +
        `Site ${engine.postcodeDistrict(answers.district) || 'not given'}, ground floor with level access: ${answers.ground || 'not given'}, ` +
        `three-phase power: ${answers.power || 'not given'}. Start: ${WHEN_TEXT[answers.when] || 'not given'}. ` +
        `Estimated ${engine.formatRange(result.low, result.high)}, subject to survey.`
      );
    }

    function estimateHandOff(trigger, action, slot) {
      const result = estimateResult();
      if (!result) return;
      const range = `${engine.formatRange(result.low, result.high)}, subject to survey`;
      toQuote(trigger, {
        action,
        source: `estimator-estimate-${action}`,
        sourceModule: 'estimate',
        service: result.quoteService,
        postcode: engine.postcodeDistrict(state.estimate.district),
        message: estimateMessage(result),
        estimateRange: range,
        estimateInputs: estimateInputs(result),
        callbackSlot: slot || '',
        emailEstimate: action === 'email',
        summary: {
          title: 'Your budget estimate',
          lines: [range, `${estimateProduct().label}, ${shortSizeText()}`].concat(slot ? [`Call me at ${slot}`] : []),
        },
      });
    }

    ACTIONS['estimate-quote'] = (target) => estimateHandOff(target, target.dataset.value);

    ACTIONS.callback = (target) => {
      const module = target.dataset.module;
      if (module === 'estimate') estimateHandOff(target, 'callback', target.dataset.value);
      else if (CALLBACK_HANDLERS[module]) CALLBACK_HANDLERS[module](target, target.dataset.value);
    };

    const CALLBACK_HANDLERS = {};

    /* Choosing an option moves straight on to the next screen. */
    const CHOOSE = {
      need(value) {
        const before = config.pricing.products[state.estimate.need];
        const after = config.pricing.products[value];
        if (before && after && (before.pricing === 'run') !== (after.pricing === 'run')) {
          state.estimate.temp = undefined;
          state.estimate.tempFrom = undefined;
        }
        state.estimate.need = value;
        if (value === 'combined' && state.estimate.temp === 'chiller') state.estimate.temp = undefined;
        go('estimate:size');
      },
      temp(value) {
        if (value === 'not_sure') {
          go('estimate:store');
          return;
        }
        state.estimate.temp = value;
        state.estimate.tempFrom = undefined;
        go('estimate:site');
      },
      store(value) {
        const product = config.sizing.products[value];
        let band = product ? product.band : 'chiller';
        if (state.estimate.need === 'combined' && band === 'chiller') band = 'freezer';
        if (state.estimate.need === 'display' && band === 'deep_freeze') band = 'freezer';
        state.estimate.temp = band;
        state.estimate.tempFrom = value;
        go('estimate:site');
      },
      when(value) {
        state.estimate.when = value;
        go('estimate:result');
      },
    };

    ACTIONS.choose = (target) => {
      const handler = CHOOSE[target.dataset.field];
      if (handler) handler(target.dataset.value, target);
    };

    ACTIONS['preset-dims'] = (target) => {
      const form = target.closest('form');
      form.elements.w.value = target.dataset.w;
      form.elements.l.value = target.dataset.l;
      if (!form.elements.h.value) form.elements.h.value = '2.2';
      showError(form, '');
      form.elements.w.focus();
    };

    ACTIONS['preset-run'] = (target) => {
      const form = target.closest('form');
      form.elements.metres.value = target.dataset.value;
      showError(form, '');
      form.elements.metres.focus();
    };

    ACTIONS['to-sizing'] = () => {
      state.sizing.from = 'estimate';
      go(FIRST_SCREEN.sizing);
    };

    FORMS.size = (form) => {
      const mode = form.elements['size-mode'].value || 'dims';
      if (mode === 'store') return;
      if (mode === 'dims') {
        const w = parseNumber(form.elements.w.value);
        const l = parseNumber(form.elements.l.value);
        const h = form.elements.h.value.trim() === '' ? 2.2 : parseNumber(form.elements.h.value);
        if (!(w >= 0.8 && w <= 40) || !(l >= 0.8 && l <= 40)) {
          showError(form, 'Enter the width and length in metres, between 0.8 and 40, for example 3 and 2.4.');
          (Number.isNaN(w) || w < 0.8 || w > 40 ? form.elements.w : form.elements.l).focus();
          return;
        }
        if (!(h >= 1.8 && h <= 10)) {
          showError(form, 'Enter the height in metres, between 1.8 and 10. Most rooms are 2.2 m inside.');
          form.elements.h.focus();
          return;
        }
        Object.assign(state.estimate, { sizeMode: 'dims', w, l, h });
      } else {
        const unit = form.elements['area-unit'].value || 'm2';
        const area = parseNumber(form.elements.area.value);
        const m2 = engine.areaFromSize({ mode: 'area', value: area, unit });
        if (!(m2 >= 1 && m2 <= 1600)) {
          showError(form, unit === 'sqft' ? 'Enter the floor area in square feet, for example 75.' : 'Enter the floor area in m², for example 7.');
          form.elements.area.focus();
          return;
        }
        Object.assign(state.estimate, { sizeMode: 'area', area, areaUnit: unit });
      }
      go('estimate:temp');
    };

    FORMS['size-run'] = (form) => {
      const metres = parseNumber(form.elements.metres.value);
      if (!(metres >= 1 && metres <= 60)) {
        showError(form, 'Enter the total length of cabinets in metres, for example 5.');
        form.elements.metres.focus();
        return;
      }
      state.estimate.metres = metres;
      go('estimate:temp');
    };

    FORMS.site = (form) => {
      const typed = form.elements.district.value;
      const district = engine.postcodeDistrict(typed);
      const ground = form.elements.ground.value;
      const power = form.elements.power.value;
      if (!district) {
        showError(form, 'Enter the first part of the site postcode, for example SE1 or CM2.');
        form.elements.district.focus();
        return;
      }
      if (!ground || !power) {
        showError(form, !ground ? 'Choose whether the room is on the ground floor with level access.' : 'Choose whether there is three-phase power.');
        form.querySelector(`input[name="${!ground ? 'ground' : 'power'}"]`).focus();
        return;
      }
      Object.assign(state.estimate, { district, ground, power });
      go('estimate:when');
    };

    /* Size answers chosen in "What size do I need" come back here at question 4. */
    function budgetForSize(size, band) {
      state.estimate.need = band === 'freezer' || band === 'deep_freeze' ? 'freezer_room' : 'cold_room';
      Object.assign(state.estimate, { sizeMode: 'dims', w: size.w, l: size.l, h: size.h || 2.2, temp: band, tempFrom: undefined });
      go('estimate:site');
    }

    /* Module 2: what size do I need */

    const FREQUENCY_OPTIONS = [
      { value: 'daily', title: 'Daily' },
      { value: 'two_three', title: '2 to 3 times a week' },
      { value: 'weekly', title: 'Weekly' },
    ];
    const FREQUENCY_TEXT = { daily: 'daily', two_three: '2 to 3 times a week', weekly: 'weekly' };

    function sizingProduct() {
      return config.sizing.products[state.sizing.product] || null;
    }

    function sizingResult() {
      const product = sizingProduct();
      if (!product) return null;
      return engine.recommendSize(
        { product: state.sizing.product, quantity: state.sizing.quantity, frequency: product.basis === 'stored' ? 'weekly' : state.sizing.frequency },
        config
      );
    }

    function halfText(value) {
      return Number.isInteger(value) ? String(value) : value.toFixed(1);
    }

    function photoUrl(name, width) {
      const base = settings.filesBase || '/cdn/shop/files/';
      return `${base}${encodeURIComponent(name)}?width=${width}`;
    }

    SCREENS['sizing:product'] = {
      module: 'sizing',
      step: 1,
      steps: 3,
      render() {
        const products = config.sizing.products;
        const options = Object.keys(products).map((key) => ({ value: key, title: products[key].label, icon: products[key].icon }));
        return heading('What will you store?', 'Choose the main thing. If it is a mix, choose Mixed stock.') + optionList('product', options, state.sizing.product, true);
      },
    };

    SCREENS['sizing:quantity'] = {
      module: 'sizing',
      step: 2,
      steps: 3,
      render() {
        const product = sizingProduct();
        if (!product) return heading('Choose what you store first');
        const value = Number.isFinite(state.sizing.quantity) && state.sizing.product === state.sizing.quantityFor ? state.sizing.quantity : product.default;
        return (
          heading('How much?', product.basis === 'stored' ? 'How much do you keep in the room at any one time?' : 'Roughly how much goes through the room in a normal busy week?') +
          '<form class="ac-est-form" data-form="quantity" novalidate>' +
          '<div class="ac-est-stepper">' +
          `<label class="ac-est-field__label" for="ac-est-qty">${escapeHtml(product.quantity_label)}</label>` +
          '<div class="ac-est-stepper__row">' +
          `<button type="button" class="ac-est-stepper__button" data-action="step" data-value="-1" aria-label="Fewer ${escapeHtml(product.unit)}">−</button>` +
          `<input class="ac-est-field__input ac-est-stepper__input" id="ac-est-qty" name="quantity" type="text" inputmode="numeric" autocomplete="off" value="${value}" aria-describedby="ac-est-qty-hint" data-min="${product.min}" data-max="${product.max}" data-step="${product.step}">` +
          `<button type="button" class="ac-est-stepper__button" data-action="step" data-value="1" aria-label="More ${escapeHtml(product.unit)}">+</button>` +
          '</div>' +
          `<input class="ac-est-slider" type="range" min="${product.min}" max="${product.max}" step="${product.step}" value="${Math.min(Math.max(value, product.min), product.max)}" aria-label="${escapeHtml(product.quantity_label)}" data-ac-est-slider>` +
          `<p class="ac-est-field__hint" id="ac-est-qty-hint">${escapeHtml(product.helper)}</p>` +
          '</div>' +
          errorLine() +
          continueButton() +
          '</form>'
        );
      },
    };

    SCREENS['sizing:frequency'] = {
      module: 'sizing',
      step: 3,
      steps: 3,
      render() {
        return heading('How often do you get deliveries?', 'More frequent deliveries mean less stock to hold, so a smaller room.') + optionList('frequency', FREQUENCY_OPTIONS, state.sizing.frequency);
      },
    };

    function fitSentence(fit, size) {
      const roomText = `a ${size.w.toFixed(1)} × ${size.l.toFixed(1)} m room`;
      const need = `needs about ${fit.needW.toFixed(1)} × ${fit.needL.toFixed(1)} m of clear floor and ${fit.needH.toFixed(1)} m of height, plus about ${fit.doorClearance.toFixed(1)} m clear in front of the door`;
      if (fit.fits && fit.reason === 'ok') return { verdict: 'Yes, it fits.', text: `${roomText.charAt(0).toUpperCase()}${roomText.slice(1)} ${need}.` };
      if (fit.fits) {
        return {
          verdict: 'Yes, with a lower room.',
          text: `Your ceiling leaves space for a ${fit.internalHeight.toFixed(1)} m internal height instead of the usual 2.2 m, once the roof panel and the refrigeration unit are allowed for.`,
        };
      }
      if (fit.reason === 'height') {
        return {
          verdict: 'No, the ceiling is too low.',
          text: `A cold room needs at least ${fit.minH.toFixed(1)} m of ceiling height: 2.0 m inside plus the roof panel and the refrigeration unit. We can look at other layouts at the survey.`,
        };
      }
      const alternatives = [];
      if (fit.nextDown) alternatives.push(`the next size down, ${fit.nextDown.w.toFixed(1)} × ${fit.nextDown.l.toFixed(1)} m,`);
      if (fit.proportion) alternatives.push(`a made-to-measure ${fit.proportion.w.toFixed(1)} × ${fit.proportion.l.toFixed(1)} m room with the same floor area`);
      let suggestion = 'We can look at a made-to-measure room at the survey.';
      if (alternatives.length) {
        const joined = alternatives.join(' or ');
        suggestion = `${joined.charAt(0).toUpperCase()}${joined.slice(1)} would fit.`;
      }
      return {
        verdict: 'No, not as it is.',
        text: `${roomText.charAt(0).toUpperCase()}${roomText.slice(1)} needs about ${fit.needW.toFixed(1)} × ${fit.needL.toFixed(1)} m of clear floor. ${suggestion}`,
      };
    }

    function fitBlock(result) {
      const space = state.sizing.space;
      if (!space) return '';
      const size = result.sizes.find((item) => item.id === (result.recommendedId || result.sizes[result.sizes.length - 1].id));
      const fit = engine.checkFit({ w: size.w, l: size.l }, space, config);
      const sentence = fitSentence(fit, size);
      return (
        `<div class="ac-est-fit ac-est-fit--${fit.fits ? 'yes' : 'no'}">` +
        `<p class="ac-est-fit__verdict">${icon(fit.fits ? 'check' : 'cross', 22)}<span>${escapeHtml(sentence.verdict)}</span></p>` +
        `<p class="ac-est-fit__text">${escapeHtml(sentence.text)} Confirmed at survey.</p>` +
        '</div>'
      );
    }

    SCREENS['sizing:result'] = {
      module: 'sizing',
      step: 3,
      steps: 3,
      result: true,
      render() {
        const result = sizingResult();
        if (!result) {
          return heading('We need a little more') + '<button type="button" class="ac-btn ac-btn--secondary" data-action="restart" data-value="sizing">Start again</button>';
        }
        const product = sizingProduct();
        const bandText = bandLabel(result.band);
        const space = state.sizing.space || {};
        const checkSize = result.sizes.find((item) => item.id === (result.recommendedId || result.sizes[result.sizes.length - 1].id));

        const cards = result.sizes
          .map((size) => {
            const holds =
              `Holds about ${size.holds} ${product.unit} at a time` +
              (product.unit !== 'pallets' && size.pallets >= 1 ? `, or about ${size.pallets} pallet${size.pallets === 1 ? '' : 's'}` : '');
            return (
              `<li class="ac-est-size${size.recommended ? ' is-recommended' : ''}">` +
              `<img class="ac-est-size__photo" src="${escapeHtml(photoUrl(size.image, 480))}" srcset="${escapeHtml(photoUrl(size.image, 320))} 320w, ${escapeHtml(
                photoUrl(size.image, 480)
              )} 480w, ${escapeHtml(photoUrl(size.image, 640))} 640w" sizes="(min-width: 750px) 180px, 100vw" width="480" height="320" loading="lazy" alt="${escapeHtml(size.alt)}">` +
              '<div class="ac-est-size__body">' +
              (size.recommended ? '<p class="ac-est-size__badge">Recommended</p>' : '') +
              `<h3 class="ac-est-size__title">${size.w.toFixed(1)} × ${size.l.toFixed(1)} m</h3>` +
              `<p class="ac-est-size__area">${size.area.toFixed(1)} m² inside</p>` +
              `<p class="ac-est-size__holds">${escapeHtml(holds)}</p>` +
              `<button type="button" class="ac-btn ${size.recommended ? 'ac-btn--primary' : 'ac-btn--secondary'} ac-btn--sm" data-action="size-budget" data-value="${size.id}">Get a budget for this size</button>` +
              '</div></li>'
            );
          })
          .join('');

        return (
          '<div class="ac-est-result" role="group" aria-labelledby="ac-estimator-heading">' +
          `<h2 class="ac-est-result__range" tabindex="-1" data-ac-est-heading>Recommended floor area: about ${halfText(result.areaLow)} to ${halfText(
            result.areaHigh
          )} m², subject to survey</h2>` +
          `<p class="ac-est-result__meta">${escapeHtml(bandText)} · for about ${result.held} ${escapeHtml(product.unit)} held at a time${
            product.basis === 'stored' ? '' : `, deliveries ${escapeHtml(FREQUENCY_TEXT[state.sizing.frequency] || '')}`
          }</p>` +
          `<p class="ac-est-result__note">${escapeHtml(result.storageNote)}</p>` +
          (result.placeholder ? `<p class="ac-est-flag">${icon('info', 18)}<span>Sizes are illustrative until our sizing rules are confirmed.</span></p>` : '') +
          '</div>' +
          (result.beyond
            ? '<p class="ac-est-text">That is more than our largest standard room. We design cold stores like this to suit your stock and building: book a free site survey and we will size it with you.</p>'
            : '') +
          `<h3 class="ac-est-subheading">${result.beyond ? 'Our largest standard sizes' : 'Standard sizes closest to your answer'}</h3>` +
          `<ul class="ac-est-sizes" role="list">${cards}</ul>` +
          '<div class="ac-est-fitcheck">' +
          '<h3 class="ac-est-subheading">Will it fit?</h3>' +
          `<p class="ac-est-text">Enter the floor space and ceiling height you have for the ${checkSize.w.toFixed(1)} × ${checkSize.l.toFixed(1)} m room. We allow for the panels, a service gap and the refrigeration unit.</p>` +
          '<form class="ac-est-form" data-form="fit" novalidate>' +
          '<div class="ac-est-fields ac-est-fields--three">' +
          textField('ac-est-space-w', 'w', 'Width', trimNumber(space.w), { unit: 'm' }) +
          textField('ac-est-space-l', 'l', 'Length', trimNumber(space.l), { unit: 'm' }) +
          textField('ac-est-space-h', 'h', 'Ceiling', trimNumber(space.h), { unit: 'm' }) +
          '</div>' +
          errorLine() +
          '<div class="ac-est-next"><button type="submit" class="ac-btn ac-btn--secondary">Check the fit</button></div>' +
          `<div data-ac-est-fit aria-live="polite">${fitBlock(result)}</div>` +
          '</form></div>' +
          '<div class="ac-est-actions">' +
          '<button type="button" class="ac-btn ac-btn--quote ac-btn--lg" data-action="sizing-quote">Book a free site survey</button>' +
          `<button type="button" class="ac-btn ac-btn--secondary ac-btn--lg" data-action="size-budget" data-value="${checkSize.id}">Get a budget for this size</button>` +
          '</div>' +
          callLine('sizing') +
          '<p class="ac-est-restart"><button type="button" class="ac-est-textbutton" data-action="restart" data-value="sizing">Size a different room</button></p>'
        );
      },
      onResult() {
        const result = sizingResult();
        if (!result) return;
        const key = `sizing:${state.sizing.product}:${state.sizing.quantity}:${state.sizing.frequency}`;
        if (key === lastResult) return;
        lastResult = key;
        track('estimator_result', { module: 'sizing', size_bucket: engine.sizeBucket(result.area), out_of_hours: !officeOpen() });
      },
    };

    function sizingSummary(result) {
      const product = sizingProduct();
      const size = result.sizes.find((item) => item.id === (result.recommendedId || result.sizes[result.sizes.length - 1].id));
      const space = state.sizing.space;
      let fitText = '';
      if (space) {
        const fit = engine.checkFit({ w: size.w, l: size.l }, space, config);
        fitText = ` Space available ${trimNumber(space.w)} × ${trimNumber(space.l)} m, ceiling ${trimNumber(space.h)} m: ${fit.fits ? 'fits' : 'does not fit as it is'}.`;
      }
      return {
        size,
        message:
          `Size check: ${product.label.toLowerCase()}, ${state.sizing.quantity} ${product.unit}${product.basis === 'stored' ? ' stored' : ` a week, deliveries ${FREQUENCY_TEXT[state.sizing.frequency]}`}. ` +
          `Recommended about ${halfText(result.areaLow)} to ${halfText(result.areaHigh)} m², ${bandLabel(result.band)}. Suggested size ${size.w.toFixed(1)} × ${size.l.toFixed(1)} m.${fitText}`,
        inputs: JSON.stringify({
          m: 'sizing',
          product: state.sizing.product,
          qty: state.sizing.quantity,
          freq: product.basis === 'stored' ? 'stored' : state.sizing.frequency,
          size: size.id,
          space: space || null,
        }),
      };
    }

    ACTIONS['sizing-quote'] = (target) => {
      const result = sizingResult();
      if (!result) return;
      const summary = sizingSummary(result);
      toQuote(target, {
        action: 'size',
        source: 'estimator-sizing-survey',
        sourceModule: 'sizing',
        service: result.band === 'chiller' ? 'New cold room' : 'New freezer room or blast freezer',
        message: summary.message,
        estimateInputs: summary.inputs,
        summary: {
          title: 'Your size check',
          lines: [`About ${halfText(result.areaLow)} to ${halfText(result.areaHigh)} m², subject to survey`, `Suggested ${summary.size.w.toFixed(1)} × ${summary.size.l.toFixed(1)} m, ${lowerFirst(bandLabel(result.band))}`],
        },
      });
    };

    ACTIONS['size-budget'] = (target) => {
      const result = sizingResult();
      if (!result) return;
      const size = result.sizes.find((item) => item.id === target.dataset.value);
      if (!size) return;
      let height = config.sizing.fit.default_internal_height_m;
      if (state.sizing.space) {
        const fit = engine.checkFit({ w: size.w, l: size.l }, state.sizing.space, config);
        if (fit.fits) height = fit.internalHeight;
      }
      budgetForSize({ w: size.w, l: size.l, h: height }, result.band);
    };

    ACTIONS.step = (target) => {
      const form = target.closest('form');
      const input = form.elements.quantity;
      const slider = form.querySelector('[data-ac-est-slider]');
      const step = Number(input.dataset.step) || 1;
      const min = Number(input.dataset.min) || 0;
      const max = Number(input.dataset.max) || 1000;
      const current = parseNumber(input.value);
      const next = Math.min(max, Math.max(min, (Number.isFinite(current) ? current : min) + step * Number(target.dataset.value)));
      input.value = String(next);
      if (slider) slider.value = String(next);
      showError(form, '');
    };

    CHOOSE.product = (value) => {
      if (state.sizing.product !== value) {
        state.sizing.quantity = undefined;
        state.sizing.quantityFor = undefined;
      }
      state.sizing.product = value;
      go('sizing:quantity');
    };

    CHOOSE.frequency = (value) => {
      state.sizing.frequency = value;
      go('sizing:result');
    };

    FORMS.finance = (form) => updateFinance(form, form.elements.amount);

    FORMS.quantity = (form) => {
      const input = form.elements.quantity;
      const value = parseNumber(input.value);
      const min = Number(input.dataset.min);
      const max = Number(input.dataset.max);
      if (!(value >= min && value <= max)) {
        showError(form, value > max ? `For more than ${max}, book a free site survey and we will size the room with you.` : `Enter a number from ${min} to ${max}.`);
        input.focus();
        return;
      }
      state.sizing.quantity = Math.round(value);
      state.sizing.quantityFor = state.sizing.product;
      const product = sizingProduct();
      go(product && product.basis === 'stored' ? 'sizing:result' : 'sizing:frequency');
    };

    FORMS.fit = (form) => {
      const w = parseNumber(form.elements.w.value);
      const l = parseNumber(form.elements.l.value);
      const h = parseNumber(form.elements.h.value);
      if (!(w >= 0.5 && w <= 100) || !(l >= 0.5 && l <= 100)) {
        showError(form, 'Enter the width and length of the space in metres, for example 3.5 and 3.');
        (w >= 0.5 && w <= 100 ? form.elements.l : form.elements.w).focus();
        return;
      }
      if (!(h >= 1.5 && h <= 20)) {
        showError(form, 'Enter the ceiling height in metres, for example 2.7.');
        form.elements.h.focus();
        return;
      }
      state.sizing.space = { w, l, h };
      save();
      const result = sizingResult();
      const holder = form.querySelector('[data-ac-est-fit]');
      if (holder && result) holder.innerHTML = fitBlock(result);
    };

    /* Module 4: spread the cost. Hidden until the theme setting is on (finance partner not yet confirmed). */

    function financeAmounts() {
      const finance = state.finance;
      if (finance.source !== 'typed') {
        const estimate = state.estimate.need ? estimateResult() : null;
        if (estimate) return { low: estimate.low, high: estimate.high, from: 'estimate', estimate };
      }
      const typed = finance.amount;
      if (Number.isFinite(typed) && typed > 0) return { low: typed, high: typed, from: 'typed' };
      return null;
    }

    function financeBlock() {
      const amounts = financeAmounts();
      const term = state.finance.term || config.finance.default_term_months;
      if (!amounts) return '<p class="ac-est-text">Enter an amount to see an illustrative monthly cost.</p>';
      const range = engine.financeRange(amounts.low, amounts.high, term, config);
      if (!range) return '';
      return (
        '<div class="ac-est-result ac-est-result--finance">' +
        `<p class="ac-est-result__range">About ${escapeHtml(engine.formatRange(range.low, range.high))} a month over ${term} months, subject to survey</p>` +
        `<p class="ac-est-result__meta">For ${escapeHtml(
          amounts.from === 'estimate' ? `your budget estimate of ${engine.formatRange(amounts.low, amounts.high)}` : `${engine.formatMoney(amounts.low)}`
        )} · ${escapeHtml(config.vat_note || 'excluding VAT')}</p>` +
        '<p class="ac-est-result__note">Illustrative, subject to status. Finance is provided by a third party, not by Alaska Cold.</p>' +
        (range.placeholder ? `<p class="ac-est-flag">${icon('info', 18)}<span>Rates are illustrative until our finance partner confirms them.</span></p>` : '') +
        '</div>'
      );
    }

    SCREENS['finance:main'] = {
      module: 'finance',
      mount() {
        reportFinance();
      },
      render() {
        const finance = state.finance;
        const hasEstimate = Boolean(state.estimate.need && estimateResult());
        const source = hasEstimate && finance.source !== 'typed' ? 'estimate' : 'typed';
        const term = finance.term || config.finance.default_term_months;
        return (
          heading('Spread the cost', 'See what a cold room could cost a month on finance. Nothing here is an offer of credit.') +
          '<form class="ac-est-form" data-form="finance" novalidate>' +
          (hasEstimate
            ? segment(
                'finance-source',
                'Amount to spread',
                [
                  { value: 'estimate', title: 'My budget estimate' },
                  { value: 'typed', title: 'Type an amount' },
                ],
                source
              )
            : '') +
          `<div data-panel="typed"${source === 'typed' ? '' : ' hidden'}>` +
          textField('ac-est-finance-amount', 'amount', 'Amount', Number.isFinite(finance.amount) ? String(finance.amount) : '', {
            unit: '£, excluding VAT',
            inputmode: 'numeric',
            className: 'ac-est-field--short',
          }) +
          '</div>' +
          segment(
            'finance-term',
            'Over how long?',
            config.finance.terms_months.map((months) => ({ value: String(months), title: `${months} months` })),
            String(term)
          ) +
          errorLine() +
          `<div data-ac-est-finance aria-live="polite">${financeBlock()}</div>` +
          '</form>' +
          '<div class="ac-est-actions">' +
          '<button type="button" class="ac-btn ac-btn--quote ac-btn--lg" data-action="finance-quote">Ask about finance</button>' +
          (hasEstimate ? '' : '<button type="button" class="ac-btn ac-btn--secondary ac-btn--lg" data-action="module" data-value="estimate">Get a budget estimate first</button>') +
          '</div>' +
          callLine('finance')
        );
      },
    };

    function updateFinance(form, changed) {
      const sourceInput = form.querySelector('input[name="finance-source"]:checked');
      if (sourceInput) state.finance.source = sourceInput.value;
      else state.finance.source = 'typed';
      const termInput = form.querySelector('input[name="finance-term"]:checked');
      if (termInput) state.finance.term = Number(termInput.value);

      const panel = form.querySelector('[data-panel="typed"]');
      if (panel) panel.hidden = state.finance.source !== 'typed';

      const amountInput = form.elements.amount;
      if (amountInput) {
        const value = parseNumber(String(amountInput.value).replace(/[£,\s]/g, ''));
        const limits = config.finance;
        if (amountInput.value.trim() === '') {
          state.finance.amount = undefined;
          showError(form, '');
        } else if (!(value >= limits.min_amount && value <= limits.max_amount)) {
          state.finance.amount = undefined;
          if (changed === amountInput) {
            showError(form, `Enter an amount from ${engine.formatMoney(limits.min_amount)} to ${engine.formatMoney(limits.max_amount)}.`);
          }
        } else {
          state.finance.amount = Math.round(value);
          showError(form, '');
        }
      }

      save();
      const holder = form.querySelector('[data-ac-est-finance]');
      if (holder) holder.innerHTML = financeBlock();
      reportFinance();
    }

    let financeReported = '';
    function reportFinance() {
      const amounts = financeAmounts();
      if (!amounts) return;
      const key = `${amounts.low}:${amounts.high}:${state.finance.term}`;
      if (key === financeReported) return;
      financeReported = key;
      track('estimator_result', {
        module: 'finance',
        range_bucket: engine.bucketFor((amounts.low + amounts.high) / 2, config.pricing.range_buckets),
        term_months: state.finance.term || config.finance.default_term_months,
        out_of_hours: !officeOpen(),
      });
    }

    ACTIONS['finance-quote'] = (target) => {
      const amounts = financeAmounts();
      const term = state.finance.term || config.finance.default_term_months;
      const range = amounts ? engine.financeRange(amounts.low, amounts.high, term, config) : null;
      const monthly = range ? `About ${engine.formatRange(range.low, range.high)} a month over ${term} months, illustrative` : '';
      const estimate = amounts && amounts.estimate;
      toQuote(target, {
        action: 'finance',
        source: 'estimator-finance',
        sourceModule: 'finance',
        service: estimate ? estimate.quoteService : '',
        postcode: estimate ? engine.postcodeDistrict(state.estimate.district) : '',
        message: `Finance enquiry: ${amounts ? `${amounts.from === 'estimate' ? `budget estimate ${engine.formatRange(amounts.low, amounts.high)}` : engine.formatMoney(amounts.low)}, ` : ''}over ${term} months.${
          monthly ? ` ${monthly}.` : ''
        }`,
        estimateRange: estimate ? `${engine.formatRange(estimate.low, estimate.high)}, subject to survey` : '',
        estimateInputs: JSON.stringify({ m: 'finance', amount: amounts ? [amounts.low, amounts.high] : null, term }),
        summary: { title: 'Spread the cost', lines: [monthly || `Over ${term} months`, 'Subject to status, finance from a third party'] },
      });
    };

    /* Ask a question (Step L, Phase 1): answers from the site's own content, or a hand-off to the team. */

    const askEngine = () => window.AlaskaColdAsk || null;
    let askIndex = null;
    let knowledgeRequest = null;
    let askOpenedFor = '';

    function loadKnowledge() {
      if (askIndex) return Promise.resolve(askIndex);
      if (knowledgeRequest) return knowledgeRequest;
      const engineApi = askEngine();
      if (!engineApi) return Promise.reject(new Error('Ask engine missing'));

      const cached = (() => {
        try {
          return window.sessionStorage.getItem(KNOWLEDGE_KEY);
        } catch (error) {
          return null;
        }
      })();
      const parse = (text) => {
        const start = text.indexOf('{');
        const end = text.lastIndexOf('}');
        return JSON.parse(text.slice(start, end + 1));
      };
      if (cached) {
        try {
          askIndex = engineApi.prepare(parse(cached));
          return Promise.resolve(askIndex);
        } catch (error) {
          /* fetch it again */
        }
      }

      /* Up to three tries: a slow connection, or a password page in front of the store, can fail once. */
      const fetchKnowledge = (attempt) =>
        fetch(settings.knowledgeUrl || '/pages/knowledge', { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok || /\/password/.test(response.url)) throw new Error(`Knowledge: ${response.status}`);
            return response.text();
          })
          .catch((error) => {
            if (attempt >= 3) throw error;
            return new Promise((resolve) => window.setTimeout(resolve, 600 * attempt)).then(() => fetchKnowledge(attempt + 1));
          });

      knowledgeRequest = fetchKnowledge(1)
        .then((text) => {
          const knowledge = parse(text);
          askIndex = engineApi.prepare(knowledge);
          try {
            window.sessionStorage.setItem(KNOWLEDGE_KEY, JSON.stringify(knowledge));
          } catch (error) {
            /* kept for this page only */
          }
          return askIndex;
        })
        .catch((error) => {
          knowledgeRequest = null;
          throw error;
        });
      return knowledgeRequest;
    }

    function askContext() {
      const estimate = state.estimate.need ? estimateResult() : null;
      const handle = state.ask.contextHandle || settings.pageHandle || '';
      const type = state.ask.contextType || settings.pageType;
      return {
        handle,
        guide: type === 'guide' ? handle : '',
        pageType: settings.pageType || 'other',
        financeOn: showFinance,
        estimate: estimate ? { need: state.estimate.need, area: estimate.area, range: engine.formatRange(estimate.low, estimate.high) } : null,
      };
    }

    function unmatchedList() {
      try {
        const list = JSON.parse(window.sessionStorage.getItem(UNMATCHED_KEY) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (error) {
        return [];
      }
    }

    function rememberUnmatched(question) {
      const list = unmatchedList().filter((item) => item !== question);
      list.push(question);
      try {
        window.sessionStorage.setItem(UNMATCHED_KEY, JSON.stringify(list.slice(-10)));
      } catch (error) {
        /* not kept */
      }
    }

    const MODULE_BUTTON = {
      repair: 'Open the fault checker',
      estimate: 'Get a budget estimate',
      sizing: 'What size do I need',
      finance: 'Spread the cost',
    };

    function askItemHtml(item, position) {
      const question = `<div class="ac-ask__q"><p>${escapeHtml(item.q)}</p></div>`;
      if (item.handoff) {
        const open = officeOpen();
        let slots = '';
        if (!open) {
          const offer = engine.callbackOffer(new Date(), config.contact, 'estimate');
          slots =
            '<div class="ac-ask__slots">' +
            `<p class="ac-ask__slots-title">We are closed now. Choose a time for us to call you ${escapeHtml(offer.day.relative)} (${escapeHtml(offer.day.dateLabel)}):</p>` +
            '<div class="ac-est-callback__slots">' +
            offer.times
              .map(
                (time) =>
                  `<button type="button" class="ac-btn ac-btn--secondary ac-btn--sm" data-action="ask-callback" data-q="${position}" data-value="${escapeHtml(
                    `${time}, ${offer.day.dateLabel}`
                  )}" aria-label="Call me at ${escapeHtml(time)} ${escapeHtml(offer.day.relative)}, ${escapeHtml(offer.day.dateLabel)}">${escapeHtml(time)}</button>`
              )
              .join('') +
            '</div></div>';
        }
        return (
          question +
          '<div class="ac-ask__a ac-ask__a--handoff">' +
          '<p class="ac-ask__text">I do not have that written down. Shall I pass it to the team?</p>' +
          '<div class="ac-ask__actions">' +
          `<button type="button" class="ac-btn ac-btn--quote ac-btn--sm" data-action="ask-team" data-q="${position}">Ask the team</button>` +
          `<a class="ac-btn ac-btn--secondary ac-btn--sm" href="${escapeHtml(contact.phoneHref)}">${icon('phone', 18)}<span>Call ${escapeHtml(contact.phone)}</span></a>` +
          '</div>' +
          slots +
          '</div>'
        );
      }

      const answer = item.answer || {};
      const body = answer.facts && answer.facts.length
        ? `<ul class="ac-ask__facts">${answer.facts.map((fact) => `<li>${escapeHtml(fact)}</li>`).join('')}</ul>`
        : `<p class="ac-ask__text">${escapeHtml(answer.text)}</p>`;
      const source = answer.url
        ? `<p class="ac-ask__source"><span>From: ${escapeHtml(answer.sourceTitle || 'our site')}</span> <a href="${escapeHtml(answer.url)}" data-ask-source="${escapeHtml(answer.kind || 'page')}">Read more<span class="visually-hidden"> about ${escapeHtml(answer.sourceTitle || 'this')}</span></a></p>`
        : '';
      const links = (item.links || []).filter((link) => link.url && link.url !== answer.url);
      const linkList = links.length
        ? `<ul class="ac-ask__links" role="list">${links
            .map((link) => `<li><a href="${escapeHtml(link.url)}"${/^tel:|^mailto:|wa\.me/.test(link.url) ? '' : ' data-ask-source="coverage"'}${/wa\.me/.test(link.url) ? ' target="_blank" rel="noopener"' : ''}>${escapeHtml(link.label)}</a></li>`)
            .join('')}</ul>`
        : '';
      const moduleButton = item.module && MODULE_BUTTON[item.module]
        ? `<div class="ac-ask__actions"><button type="button" class="ac-btn ac-btn--primary ac-btn--sm" data-action="ask-module" data-value="${item.module}"${
            item.moduleOptions && item.moduleOptions.fault ? ` data-fault="${escapeHtml(item.moduleOptions.fault)}"` : ''
          }>${escapeHtml(MODULE_BUTTON[item.module])}</button>${
            item.module === 'repair' ? `<a class="ac-btn ac-btn--secondary ac-btn--sm" href="${escapeHtml(contact.phoneHref)}">${icon('phone', 18)}<span>Call ${escapeHtml(contact.phone)}</span></a>` : ''
          }</div>`
        : '';
      const related = (item.related || []).length
        ? `<div class="ac-ask__related"><p class="ac-ask__related-title">Related questions</p><div class="ac-ask__chips">${item.related
            .map((entry) => `<button type="button" class="ac-ask__chip" data-action="ask-chip" data-value="${escapeHtml(entry.question)}">${escapeHtml(entry.question)}</button>`)
            .join('')}</div></div>`
        : '';
      return question + `<div class="ac-ask__a">${body}${linkList}${source}${moduleButton}${related}</div>`;
    }

    function askThreadHtml() {
      return state.ask.thread.map((item, position) => `<li class="ac-ask__item">${askItemHtml(item, position)}</li>`).join('');
    }

    function askChipsHtml(chips) {
      if (!chips.length) return '';
      return (
        '<div class="ac-ask__suggest">' +
        '<p class="ac-ask__suggest-title" id="ac-ask-suggest-title">Suggested questions</p>' +
        `<div class="ac-ask__chips" role="group" aria-labelledby="ac-ask-suggest-title">${chips
          .map((chip) => `<button type="button" class="ac-ask__chip" data-action="ask-chip" data-value="${escapeHtml(chip)}">${escapeHtml(chip)}</button>`)
          .join('')}</div></div>`
      );
    }

    SCREENS['ask:main'] = {
      module: 'ask',
      render() {
        const context = askContext();
        const reminder = context.estimate
          ? `<p class="ac-ask__reminder">${icon('estimate', 18)}<span>Your estimate: ${escapeHtml(estimateProduct().label)}, ${escapeHtml(shortSizeText())}: ${escapeHtml(
              context.estimate.range
            )}, subject to survey</span></p>`
          : '';
        const chips = askIndex ? askEngine().suggestions(askIndex, context) : [];
        return (
          '<div class="ac-ask">' +
          '<div class="ac-ask__head">' +
          heading('Ask a question', 'Answers come from our own pages. If we do not have it written down, we will pass your question to the team.') +
          reminder +
          '</div>' +
          `<ol class="ac-ask__thread" role="log" aria-live="polite" aria-relevant="additions" aria-label="Questions and answers" data-ac-ask-thread>${askThreadHtml()}</ol>` +
          '<div class="ac-ask__bottom">' +
          `<div data-ac-ask-chips>${askIndex ? askChipsHtml(chips) : '<p class="ac-ask__loading">Loading suggested questions</p>'}</div>` +
          '<form class="ac-ask__form" data-form="ask" novalidate>' +
          '<label class="visually-hidden" for="ac-ask-input">Your question</label>' +
          '<input class="ac-ask__input" id="ac-ask-input" name="question" type="text" maxlength="200" autocomplete="off" placeholder="Type your question" enterkeyhint="send">' +
          `<button type="submit" class="ac-btn ac-btn--primary ac-ask__send" aria-label="Send question">${icon('send', 20)}<span class="ac-ask__send-label">Send</span></button>` +
          '</form>' +
          '</div>' +
          '</div>'
        );
      },
      mount(container) {
        const openKey = `${settings.pageType}:${settings.pageHandle}`;
        if (askOpenedFor !== openKey) {
          askOpenedFor = openKey;
          track('ask_open', { source_page_type: settings.pageType || 'other' });
        }
        const thread = container.querySelector('[data-ac-ask-thread]');
        if (thread) thread.scrollTop = thread.scrollHeight;
        if (!askIndex) {
          loadKnowledge()
            .then(() => {
              const holder = body.querySelector('[data-ac-ask-chips]');
              if (holder && state.screen === 'ask:main') holder.innerHTML = askChipsHtml(askEngine().suggestions(askIndex, askContext()));
            })
            .catch(() => {
              const holder = body.querySelector('[data-ac-ask-chips]');
              if (holder) holder.innerHTML = '<p class="ac-ask__loading">Questions could not load just now. You can still type yours, or call us.</p>';
            });
        }
      },
    };

    function storeItem(question, reply) {
      const answer = reply.answer
        ? { text: reply.answer.text, facts: reply.answer.facts || null, url: reply.answer.url, sourceTitle: reply.answer.sourceTitle, kind: reply.answer.kind }
        : null;
      return {
        q: question,
        intent: reply.intent,
        matched: reply.matched,
        handoff: reply.handoff,
        answer,
        links: reply.links || [],
        module: reply.module,
        moduleOptions: reply.moduleOptions,
        related: reply.related || [],
      };
    }

    function askQuestion(question) {
      const text = String(question || '').replace(/\s+/g, ' ').trim().slice(0, 200);
      if (!text) return;
      const engineApi = askEngine();
      const run = (index) => {
        const reply = index ? engineApi.answer(index, text, askContext()) : { intent: 'unavailable', matched: false, handoff: true, score: 0, bucket: 'none' };
        state.ask.thread.push(storeItem(text, reply));
        state.ask.thread = state.ask.thread.slice(-20);
        save();
        track('ask_question', { intent: reply.intent, matched: Boolean(reply.matched && !reply.handoff), score_bucket: reply.bucket || 'none' });
        if (reply.handoff) {
          rememberUnmatched(text);
          track('ask_unmatched', { question: engineApi ? engineApi.analyticsText(text) : '' });
        }
        const thread = body.querySelector('[data-ac-ask-thread]');
        if (thread) {
          const item = document.createElement('li');
          item.className = 'ac-ask__item';
          item.innerHTML = askItemHtml(state.ask.thread[state.ask.thread.length - 1], state.ask.thread.length - 1);
          thread.appendChild(item);
          thread.scrollTop = thread.scrollHeight;
          item.scrollIntoView({ block: 'nearest' });
        }
      };
      loadKnowledge()
        .then((index) => run(index))
        .catch(() => run(null));
    }

    FORMS.ask = (form) => {
      const input = form.elements.question;
      const value = input.value;
      input.value = '';
      askQuestion(value);
      input.focus();
    };

    ACTIONS['ask-chip'] = (target) => {
      askQuestion(target.dataset.value);
      const input = body.querySelector('#ac-ask-input');
      if (input) input.focus({ preventScroll: true });
    };

    ACTIONS['ask-module'] = (target) => {
      const module = target.dataset.value;
      track('ask_to_module', { module });
      state.history = ['home', 'ask:main'];
      if (module === 'repair' && target.dataset.fault && config.triage.faults[target.dataset.fault]) {
        state.repair.fault = target.dataset.fault;
        state.history.push(FIRST_SCREEN.repair);
        state.screen = 'repair:risk';
      } else {
        state.screen = FIRST_SCREEN[module];
      }
      save();
      render();
    };

    function askHandOff(trigger, position, slot) {
      const item = state.ask.thread[position];
      if (!item) return;
      track('ask_to_quote', { callback: Boolean(slot) });
      toQuote(trigger, {
        action: slot ? 'ask_callback' : 'ask',
        source: 'estimator-ask',
        sourceModule: 'ask',
        service: 'Something else',
        questionText: item.q,
        callbackSlot: slot || '',
        summary: { title: 'Your question', lines: [item.q].concat(slot ? [`Call me at ${slot}`] : []) },
      });
    }

    ACTIONS['ask-team'] = (target) => askHandOff(target, Number(target.dataset.q));
    ACTIONS['ask-callback'] = (target) => askHandOff(target, Number(target.dataset.q), target.dataset.value);

    /* Module 3: my cold room has a problem. Triage, not diagnosis. */

    const RISK_OPTIONS = [
      { value: 'yes', title: 'Yes', text: 'Stock is warming up, or the room has stopped' },
      { value: 'no', title: 'No', text: 'Stock is safe for now, but something is wrong' },
      { value: 'unsure', title: 'Not sure' },
    ];

    function repairLabels() {
      const triage = config.triage;
      const repair = state.repair;
      const fault = triage.faults[repair.fault];
      return {
        fault: fault ? fault.label : '',
        type: triage.types[repair.type] || '',
        risk: repair.atRisk === 'yes' ? 'yes' : repair.atRisk === 'no' ? 'no' : 'not sure',
        district: engine.postcodeDistrict(repair.district),
      };
    }

    function faultSummary() {
      const labels = repairLabels();
      return (
        `${labels.fault}. Equipment: ${labels.type ? labels.type.toLowerCase() : 'not given'}. Stock at risk: ${labels.risk}.` +
        (labels.district ? ` Postcode district: ${labels.district}.` : '')
      );
    }

    function repairSteps() {
      const triage = config.triage;
      const repair = state.repair;
      const fault = triage.faults[repair.fault];
      if (!fault) return [];
      const steps = fault.steps.slice();
      if (repair.atRisk === 'yes') {
        const frozen = repair.type === 'freezer_room' || repair.type === 'cold_store';
        const stockStep = triage.stock_at_risk_steps[frozen ? 'frozen' : 'chilled'];
        if (stockStep) return [stockStep].concat(steps.slice(0, 2));
      }
      return steps.slice(0, 3);
    }

    function whatsappLink() {
      const labels = repairLabels();
      const text =
        `Hello Alaska Cold, I have a fault: ${labels.fault}. Equipment: ${labels.type ? labels.type.toLowerCase() : 'not given'}. ` +
        `Stock at risk: ${labels.risk}.${labels.district ? ` Postcode: ${labels.district}.` : ''} Here is a photo.`;
      return `https://wa.me/${encodeURIComponent(contact.whatsapp)}?text=${encodeURIComponent(text)}`;
    }

    SCREENS['repair:fault'] = {
      module: 'repair',
      step: 1,
      steps: 3,
      render() {
        const faults = config.triage.faults;
        const options = Object.keys(faults).map((key) => ({ value: key, title: faults[key].label }));
        return heading('What is happening?', 'Choose the closest. We will show you what to do right now.') + optionList('fault', options, state.repair.fault);
      },
    };

    SCREENS['repair:risk'] = {
      module: 'repair',
      step: 2,
      steps: 3,
      render() {
        return heading('Is stock at risk right now?') + optionList('risk', RISK_OPTIONS, state.repair.atRisk);
      },
    };

    SCREENS['repair:type'] = {
      module: 'repair',
      step: 3,
      steps: 3,
      render() {
        const types = config.triage.types;
        const options = Object.keys(types).map((key) => ({ value: key, title: types[key] }));
        return (
          heading('What type of equipment?') +
          '<div class="ac-est-form ac-est-form--inline">' +
          textField('ac-est-repair-district', 'district', 'Postcode district (optional)', state.repair.district || '', {
            hint: 'First part only, for example N7. It helps us send the nearest engineer.',
            inputmode: 'text',
            maxlength: 8,
            autocapitalize: 'characters',
            className: 'ac-est-field--short',
          }) +
          '</div>' +
          optionList('type', options, state.repair.type)
        );
      },
    };

    SCREENS['repair:result'] = {
      module: 'repair',
      step: 3,
      steps: 3,
      result: true,
      render() {
        const triage = config.triage;
        const fault = triage.faults[state.repair.fault];
        if (!fault) {
          return heading('Tell us what is happening') + '<button type="button" class="ac-btn ac-btn--secondary" data-action="restart" data-value="repair">Start again</button>';
        }

        const labels = repairLabels();
        const open = officeOpen();
        const atRisk = state.repair.atRisk === 'yes';
        const repairOffer = engine.callbackOffer(new Date(), config.contact, 'repair');
        const day = repairOffer.day;
        const callbackTime = repairOffer.times[0];

        const tags = [labels.fault, labels.type, atRisk ? 'Stock at risk' : '', labels.district].filter(Boolean);

        let actions = '';
        if (open) {
          actions =
            '<div class="ac-est-actions ac-est-actions--stack">' +
            `<a class="ac-btn ac-btn--quote ac-btn--lg ac-est-call" href="${escapeHtml(contact.phoneHref)}">${icon('phone', 22)}<span>Call now ${escapeHtml(contact.phone)}</span></a>` +
            `<a class="ac-btn ac-btn--secondary ac-btn--lg" href="${escapeHtml(whatsappLink())}" target="_blank" rel="noopener">${icon('whatsapp', 22)}<span>Send a photo on WhatsApp</span><span class="visually-hidden"> (opens in a new tab)</span></a>` +
            '</div>' +
            '<p class="ac-est-restart"><button type="button" class="ac-est-textbutton" data-action="repair-quote" data-value="repair">Cannot talk now? Ask us to call you back</button></p>';
        } else {
          actions =
            `<p class="ac-est-closed">We are closed now. ${atRisk ? 'Stock at risk? Our 24/7 emergency line is still answered.' : 'Leave your details and we will call you first thing.'}</p>` +
            (atRisk
              ? `<div class="ac-est-actions ac-est-actions--stack"><a class="ac-btn ac-btn--quote ac-btn--lg ac-est-call" href="${escapeHtml(contact.phoneHref)}">${icon('phone', 22)}<span>Call the 24/7 line ${escapeHtml(contact.phone)}</span></a></div>`
              : '') +
            '<div class="ac-est-actions ac-est-actions--stack">' +
            `<button type="button" class="ac-btn ${atRisk ? 'ac-btn--secondary' : 'ac-btn--quote'} ac-btn--lg" data-action="callback" data-module="repair" data-value="${escapeHtml(
              `${callbackTime}, ${day.dateLabel}`
            )}">Request a call for ${escapeHtml(callbackTime)} ${escapeHtml(day.relative)}</button>` +
            `<a class="ac-btn ac-btn--secondary ac-btn--lg" href="${escapeHtml(whatsappLink())}" target="_blank" rel="noopener">${icon('whatsapp', 22)}<span>Send a photo on WhatsApp</span><span class="visually-hidden"> (opens in a new tab)</span></a>` +
            '</div>' +
            (atRisk ? '' : `<p class="ac-est-note">Stock at risk after all? Our 24/7 emergency line is answered day and night: <a href="${escapeHtml(contact.phoneHref)}">${escapeHtml(contact.phone)}</a></p>`);
        }

        return (
          `<h2 class="ac-est-heading" tabindex="-1" data-ac-est-heading>Do these three things now</h2>` +
          `<ul class="ac-est-tags" role="list">${tags.map((tag) => `<li>${escapeHtml(tag)}</li>`).join('')}</ul>` +
          `<ol class="ac-est-steps">${repairSteps().map((step) => `<li>${escapeHtml(step)}</li>`).join('')}</ol>` +
          '<div class="ac-est-checks">' +
          '<h3 class="ac-est-checks__title">What the engineer will check</h3>' +
          `<p>${escapeHtml(fault.checks)}</p>` +
          '<p class="ac-est-note">This is first aid for your stock, not a diagnosis. The engineer finds the cause and agrees a price with you before any repair.</p>' +
          '</div>' +
          actions +
          '<p class="ac-est-restart"><button type="button" class="ac-est-textbutton" data-action="restart" data-value="repair">Report a different problem</button></p>'
        );
      },
      onResult() {
        const key = `repair:${state.repair.fault}:${state.repair.atRisk}:${state.repair.type}`;
        if (key === lastResult) return;
        lastResult = key;
        track('estimator_result', {
          module: 'repair',
          fault: state.repair.fault,
          stock_at_risk: state.repair.atRisk || '',
          out_of_hours: !officeOpen(),
        });
      },
    };

    function repairHandOff(trigger, action, slot) {
      const labels = repairLabels();
      toQuote(trigger, {
        action,
        source: `estimator-repair-${action}`,
        sourceModule: 'repair',
        service: 'Repair or emergency call-out',
        postcode: labels.district,
        message: `Fault report: ${faultSummary()}`,
        faultSummary: faultSummary(),
        callbackSlot: slot || '',
        summary: {
          title: 'Your fault report',
          lines: [`${labels.fault}${labels.type ? `, ${labels.type.toLowerCase()}` : ''}`, `Stock at risk: ${labels.risk}`].concat(
            slot ? [`Call me at ${slot}`] : []
          ),
        },
      });
    }

    ACTIONS['repair-quote'] = (target) => repairHandOff(target, 'repair');
    CALLBACK_HANDLERS.repair = (target, slot) => repairHandOff(target, 'repair_callback', slot);

    CHOOSE.fault = (value) => {
      state.repair.fault = value;
      go('repair:risk');
    };

    CHOOSE.risk = (value) => {
      state.repair.atRisk = value;
      go('repair:type');
    };

    CHOOSE.type = (value) => {
      const field = body.querySelector('#ac-est-repair-district');
      state.repair.district = field ? engine.postcodeDistrict(field.value) : '';
      state.repair.type = value;
      go('repair:result');
    };

    /* Navigation */

    function dots(screen) {
      const items = [];
      for (let index = 1; index <= screen.steps; index += 1) {
        const stateClass = screen.result || index < screen.step ? ' is-done' : index === screen.step ? ' is-current' : '';
        items.push(`<li class="ac-est-dots__dot${stateClass}"></li>`);
      }
      const label = screen.result ? 'Result' : `Step ${screen.step} of ${screen.steps}`;
      return `<p class="visually-hidden">${label}</p><ol class="ac-est-dots" aria-hidden="true">${items.join('')}</ol>`;
    }

    function render(focus) {
      const screen = SCREENS[state.screen] || SCREENS.home;
      if (!SCREENS[state.screen]) state.screen = 'home';

      body.innerHTML = screen.render();
      body.classList.toggle('ac-estimator__body--ask', screen.module === 'ask');

      if (screen.steps) {
        progress.hidden = false;
        progress.innerHTML = dots(screen);
      } else {
        progress.hidden = true;
        progress.innerHTML = '';
      }

      backButton.hidden = state.history.length === 0;
      if (screen.mount) screen.mount(body);
      body.scrollTop = 0;

      const headingElement = body.querySelector('[data-ac-est-heading]');
      if (headingElement) {
        headingElement.id = 'ac-estimator-heading';
        if (focus !== false) headingElement.focus({ preventScroll: true });
      }

      track('estimator_step', { module: screen.module || 'launcher', step: state.screen });
      if (screen.result && screen.onResult) screen.onResult();
    }

    function go(screenId) {
      if (state.screen && state.screen !== screenId) state.history.push(state.screen);
      state.history = state.history.slice(-20);
      state.screen = screenId;
      save();
      render();
    }

    function back() {
      state.screen = state.history.pop() || 'home';
      save();
      render();
    }

    function startModule(module, options) {
      if (module === 'ask') {
        /* An "Ask something else" link sets the page its suggested questions come from. */
        state.ask.contextHandle = options && options.askHandle ? options.askHandle : '';
        state.ask.contextType = options && options.askType ? options.askType : '';
      }
      state.history = ['home'];
      state.screen = FIRST_SCREEN[module];
      if (module === 'repair' && options && options.fault && config.triage.faults[options.fault]) {
        state.repair.fault = options.fault;
        state.history.push(FIRST_SCREEN.repair);
        state.screen = 'repair:risk';
      }
      if (!SCREENS[state.screen]) {
        state.history = [];
        state.screen = 'home';
      }
      save();
      render();
    }

    ACTIONS.module = (target) => {
      state.history = [];
      state.screen = 'home';
      startModule(target.dataset.value);
    };

    ACTIONS.restart = (target) => {
      const module = target.dataset.value;
      if (module === 'estimate') state.estimate = Object.assign(freshState().estimate, { need: state.estimate.need });
      if (module === 'repair') state.repair = {};
      if (module === 'sizing') state.sizing = {};
      lastResult = '';
      startModule(module);
    };

    /* Opening and closing */

    function holdScroll(hold) {
      const api = siteApi();
      if (api && typeof api.holdPageScroll === 'function') api.holdPageScroll(dialog, hold);
      else document.documentElement.classList.toggle('ac-scroll-lock', hold);
    }

    function renderMessage(title, text) {
      progress.hidden = true;
      backButton.hidden = true;
      body.innerHTML = heading(title, text) + callLine('');
    }

    function open(module, opener, options) {
      returnFocusTo = opener instanceof HTMLElement ? opener : document.activeElement;
      if (!dialog.open) dialog.showModal();
      holdScroll(true);
      track('estimator_open', { module: module || 'launcher' });

      if (!config) renderMessage('Loading the estimator', '');

      loadConfig()
        .then(() => {
          let target = module;
          if (target === 'finance' && !showFinance) target = '';
          if (target) startModule(target, options);
          else render();
        })
        .catch(() => {
          renderMessage('The estimator did not load', 'Please try again in a moment, or call us and we will answer your question straight away.');
        });
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    dialog.addEventListener('close', () => {
      holdScroll(false);
      if (returnFocusTo instanceof HTMLElement && returnFocusTo.isConnected && !document.querySelector('dialog[open]')) {
        returnFocusTo.focus();
      }
      returnFocusTo = null;
    });

    /* A press that starts and ends on the backdrop closes the dialog. */
    let pressedBackdrop = false;
    dialog.addEventListener('pointerdown', (event) => {
      pressedBackdrop = event.target === dialog;
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog && pressedBackdrop) close();
      pressedBackdrop = false;
    });

    dialog.querySelector('[data-ac-estimator-close]').addEventListener('click', close);
    backButton.addEventListener('click', back);

    body.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest('[data-action]');
      if (target && ACTIONS[target.dataset.action]) {
        event.preventDefault();
        ACTIONS[target.dataset.action](target);
      }
    });

    body.addEventListener('submit', (event) => {
      event.preventDefault();
      const form = event.target;
      if (form instanceof HTMLFormElement && FORMS[form.dataset.form]) FORMS[form.dataset.form](form);
    });

    body.addEventListener('change', (event) => {
      const input = event.target;
      const financeForm = input instanceof Element ? input.closest('form[data-form="finance"]') : null;
      if (financeForm) {
        updateFinance(financeForm, input);
        return;
      }
      if (!(input instanceof HTMLInputElement) || input.name !== 'size-mode') return;
      const form = input.closest('form');
      form.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== input.value;
      });
      const next = form.querySelector('[data-ac-est-next]');
      if (next) next.hidden = input.value === 'store';
      state.estimate.sizeMode = input.value === 'store' ? state.estimate.sizeMode : input.value;
      showError(form, '');
    });

    body.addEventListener('input', (event) => {
      const target = event.target;
      const form = target instanceof Element ? target.closest('form') : null;
      if (!form) return;
      if (form.dataset.form === 'finance') {
        updateFinance(form, target);
        return;
      }
      showError(form, '');
      if (target.matches('[data-ac-est-slider]') && form.elements.quantity) form.elements.quantity.value = target.value;
      if (target.name === 'quantity') {
        const slider = form.querySelector('[data-ac-est-slider]');
        const value = parseNumber(target.value);
        if (slider && Number.isFinite(value)) slider.value = String(value);
      }
    });

    /* Calls and WhatsApp messages started from the estimator */
    dialog.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link) return;
      const module = (SCREENS[state.screen] && SCREENS[state.screen].module) || 'launcher';
      const href = link.getAttribute('href') || '';
      if (href.startsWith('tel:')) {
        track('estimator_call_click', { module });
        if (module === 'ask') track('ask_call_click', { source_page_type: settings.pageType || 'other' });
      } else if (/wa\.me\//i.test(href)) {
        track('estimator_whatsapp_click', { module });
      }
      if (link.hasAttribute('data-ask-source')) track('ask_source_click', { url: link.getAttribute('href'), kind: link.dataset.askSource });
    });

    /* Openers: data-estimator-open anywhere on the page, and the module hashes. */
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-estimator-open]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger.dataset.estimatorOpen || '', trigger, {
        fault: trigger.dataset.estimatorFault,
        askHandle: trigger.dataset.askHandle,
        askType: trigger.dataset.askType,
      });
    });

    function openFromHash() {
      const module = MODULE_HASHES[window.location.hash];
      if (!module) return;
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
      open(module, null);
    }

    window.addEventListener('hashchange', openFromHash);
    openFromHash();

    /*
     * The launcher lifts above anything Shopify fixes to the bottom of the screen that would cover it:
     * the theme preview bar, the privacy banner and the hCaptcha badge added once a form is touched.
     * The lift is written to --ac-offset-bottom, which the launcher's position adds to its own gap.
     */
    const launcher = document.querySelector('[data-ac-estimator-launcher]');
    const BOTTOM_BARS =
      '#PBarNextFrameWrapper, #preview-bar-iframe, #shopify-pc__banner, .shopify-pc__banner__dialog, #shop-hcaptcha-badge-container';

    function measureBottomBars() {
      if (!launcher) return;
      const rootStyle = document.documentElement.style;
      const current = parseFloat(rootStyle.getPropertyValue('--ac-offset-bottom')) || 0;
      const box = launcher.getBoundingClientRect();
      if (box.height === 0) return;
      const restingBottom = box.bottom + current;
      let offset = 0;

      document.querySelectorAll(BOTTOM_BARS).forEach((element) => {
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        const rect = element.getBoundingClientRect();
        if (rect.height === 0 || rect.top < window.innerHeight / 2) return;
        const overlaps = rect.left < box.right && rect.right > box.left;
        if (overlaps && rect.top < restingBottom + 4) offset = Math.max(offset, restingBottom - rect.top + 8);
      });

      rootStyle.setProperty('--ac-offset-bottom', `${Math.round(offset)}px`);
    }

    let measureTimer = 0;
    function queueMeasure() {
      window.clearTimeout(measureTimer);
      measureTimer = window.setTimeout(measureBottomBars, 200);
    }

    measureBottomBars();
    window.addEventListener('resize', queueMeasure);
    new MutationObserver(queueMeasure).observe(document.body, { childList: true });
    /* Dialog close events do not bubble, but a capturing listener still hears them. */
    document.addEventListener('close', queueMeasure, true);
    document.addEventListener('focusout', queueMeasure, true);

    /* Hooks for the other modules, which add their own screens below. */
    return { SCREENS, FORMS, ACTIONS, CHOOSE, CALLBACK_HANDLERS, budgetForSize };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(typeof window !== 'undefined' ? window : globalThis);
